import { useState, useEffect, useCallback, createContext, useContext } from "react";

/* ═══════════════════════════════════════════════
   BRAND TOKENS
═══════════════════════════════════════════════ */
const C = {
  bg:"#131317", bg2:"#1e1e23", bg3:"#27272d",
  pink:"#ea2279", pinkLight:"#ff4d99",
  amber:"#ecb051", amberDim:"#c9903a",
  cream:"#f8eedd", creamDim:"#c9b59a",
  bPink:"rgba(234,34,121,0.25)", bAmb:"rgba(236,176,81,0.3)",
};

const IMG = {
  logo:"https://static.wixstatic.com/media/8e34ba_ff594058fd1c4a5090d0172c79e14b5b~mv2.png",
  master:"https://static.wixstatic.com/media/8e34ba_8afdc3ef21be477d9e1a4c415ead5611~mv2.jpg",
  tacos:"https://static.wixstatic.com/media/8e34ba_21c76737ddd94d019ad0633e0722ff36~mv2.jpg",
  broth:"https://static.wixstatic.com/media/8e34ba_97fd39ced86341459aad3336080d649b~mv2.jpg",
  quesa:"https://static.wixstatic.com/media/8e34ba_0698ee5e7ced4ca2a9c2a079e2b9e746~mv2.jpg",
  platter:"https://static.wixstatic.com/media/8e34ba_3595aff0a84b4a9c9340095a52eaae56~mv2.jpg",
  fresh:"https://static.wixstatic.com/media/8e34ba_ef35c304ca8c483494a9c87be59969a7~mv2.jpg",
  prep:"https://static.wixstatic.com/media/8e34ba_cf5e926bca9e453c84d1d28313a417f3~mv2.jpg",
  truck:"https://static.wixstatic.com/media/8e34ba_e3baee01b8e94027a7477d67b929f996~mv2.jpg",
  bowl:"https://static.wixstatic.com/media/8e34ba_8d69d2baae3e497d993365fec10f7eb3~mv2.jpg",
};

/* ─── GLOBAL CSS ─── */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Bebas+Neue&family=Nunito:wght@300;400;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background:#131317; color:#f8eedd; font-family:'Nunito',sans-serif; overflow-x:hidden; -webkit-tap-highlight-color:transparent; }
  ::-webkit-scrollbar { width:4px; } ::-webkit-scrollbar-thumb { background:#ea2279; border-radius:2px; }
  .dance { font-family:'Dancing Script',cursive !important; }
  .bebas { font-family:'Bebas Neue',sans-serif !important; }
  .page-fade { animation:pageFade 0.35s ease both; }
  @keyframes pageFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
  .reveal  { opacity:0; transform:translateY(22px); transition:opacity 0.7s ease,transform 0.7s ease; }
  .reveal.vis { opacity:1; transform:none; }
  .reveal-l { opacity:0; transform:translateX(-20px); transition:opacity 0.7s ease,transform 0.7s ease; }
  .reveal-l.vis { opacity:1; transform:none; }
  .reveal-r { opacity:0; transform:translateX(20px); transition:opacity 0.7s ease,transform 0.7s ease; }
  .reveal-r.vis { opacity:1; transform:none; }
  @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.2} }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  @keyframes pulse { 0%,100%{box-shadow:0 0 50px rgba(234,34,121,0.18)} 50%{box-shadow:0 0 80px rgba(234,34,121,0.38)} }
  @keyframes slideDown { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:none} }
  @keyframes spin { to{transform:rotate(360deg)} }
  input, select, textarea { font-size:16px !important; }
`;

/* ─── HOOKS ─── */
function GlobalStyles() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = GLOBAL_CSS;
    document.head.appendChild(el);
    let meta = document.querySelector('meta[name="viewport"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "viewport"; document.head.appendChild(meta); }
    meta.content = "width=device-width,initial-scale=1,maximum-scale=1";
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

function useIsMobile() {
  const [mob, setMob] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  useEffect(() => {
    const h = () => setMob(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return mob;
}

function useReveal() {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add("vis"); io.unobserve(e.target); } }),
      { threshold: 0.08 }
    );
    document.querySelectorAll(".reveal,.reveal-l,.reveal-r").forEach(t => io.observe(t));
    return () => io.disconnect();
  });
}

/* ─── SHARED ATOMS ─── */
function BtnPink({ children, href, onClick, style = {} }) {
  const s = { fontFamily:"'Bebas Neue',sans-serif", fontSize:"0.93rem", letterSpacing:"0.2em", color:"#fff", background:C.pink, padding:"13px 26px", borderRadius:3, textDecoration:"none", display:"inline-flex", alignItems:"center", justifyContent:"center", border:"none", cursor:"pointer", transition:"background 0.3s", minHeight:44, ...style };
  const over = e => e.currentTarget.style.background = C.pinkLight;
  const out  = e => e.currentTarget.style.background = C.pink;
  if (href) return <a href={href} target="_blank" rel="noreferrer" style={s} onMouseOver={over} onMouseOut={out}>{children}</a>;
  return <button onClick={onClick} style={s} onMouseOver={over} onMouseOut={out}>{children}</button>;
}

function BtnOutline({ children, onClick, href, style = {} }) {
  const s = { fontFamily:"'Bebas Neue',sans-serif", fontSize:"0.93rem", letterSpacing:"0.2em", color:C.amber, border:`2px solid ${C.amber}`, padding:"13px 26px", borderRadius:3, textDecoration:"none", display:"inline-flex", alignItems:"center", justifyContent:"center", background:"transparent", cursor:"pointer", transition:"all 0.3s", minHeight:44, ...style };
  if (href) return <a href={href} target="_blank" rel="noreferrer" style={s}>{children}</a>;
  return <button onClick={onClick} style={s}>{children}</button>;
}

function SectionLabel({ children, center }) {
  return (
    <div style={{ fontSize:"0.68rem", fontWeight:700, letterSpacing:"0.32em", textTransform:"uppercase", color:C.pink, marginBottom:10, display:"flex", alignItems:"center", gap:9, justifyContent:center?"center":undefined }}>
      <span style={{ width:22, height:2, background:C.pink, display:"block", flexShrink:0 }} />{children}
    </div>
  );
}
function SectionTitle({ bebas, script, center }) {
  return (
    <div style={{ textAlign:center?"center":undefined }}>
      {bebas  && <div className="bebas" style={{ fontSize:"clamp(2.2rem,7vw,4.2rem)", letterSpacing:"0.04em", lineHeight:0.9, color:C.amber }}>{bebas}</div>}
      {script && <div className="dance" style={{ fontSize:"clamp(1.7rem,5vw,3.2rem)", fontWeight:700, color:C.cream, lineHeight:1.15 }}>{script}</div>}
    </div>
  );
}
function Divider({ center }) {
  return <div style={{ width:44, height:3, background:`linear-gradient(to right,${C.pink},${C.amber})`, margin:center?"14px auto 20px":"14px 0 20px", borderRadius:2 }} />;
}
function PageHero({ eyebrow, title, subtitle, script, imgs=[] }) {
  return (
    <div style={{ position:"relative", minHeight:"48vh", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", textAlign:"center", padding:"20px 16px" }}>
      {imgs.length > 0 && (
        <div style={{ position:"absolute", inset:0, display:"grid", gridTemplateColumns:`repeat(${Math.min(imgs.length,3)},1fr)`, opacity:0.13, filter:"saturate(0.4)" }}>
          {imgs.slice(0,3).map((s,i)=><img key={i} src={s} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />)}
        </div>
      )}
      <div style={{ position:"absolute", inset:0, background:"linear-gradient(160deg,#131317,#1a0d08,#131317)" }} />
      <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse 70% 60% at 50% 50%,rgba(234,34,121,0.13) 0%,transparent 70%)" }} />
      <div style={{ position:"relative", zIndex:2, padding:"36px 12px" }}>
        <div style={{ fontSize:"0.67rem", fontWeight:700, letterSpacing:"0.28em", textTransform:"uppercase", color:C.pink, marginBottom:11, display:"flex", alignItems:"center", justifyContent:"center", gap:9, flexWrap:"wrap" }}>
          <span style={{ width:18, height:2, background:C.pink, display:"block", flexShrink:0 }} />{eyebrow}<span style={{ width:18, height:2, background:C.pink, display:"block", flexShrink:0 }} />
        </div>
        <div className="bebas" style={{ fontSize:"clamp(3.2rem,12vw,8.5rem)", lineHeight:0.85, WebkitTextStroke:`2px ${C.amber}`, color:"transparent", letterSpacing:"0.04em" }}>{title}</div>
        {script && <div className="dance" style={{ fontSize:"clamp(1.4rem,5vw,2.8rem)", color:C.pink, marginTop:6 }}>{script}</div>}
        <div style={{ width:38, height:3, background:`linear-gradient(to right,${C.pink},${C.amber})`, margin:"14px auto", borderRadius:2 }} />
        {subtitle && <p style={{ fontSize:"0.93rem", color:C.creamDim, maxWidth:460, margin:"0 auto", lineHeight:1.7 }}>{subtitle}</p>}
      </div>
    </div>
  );
}

/* ─── NAVBAR ─── */
function Topbar({ content }) {
  if (!content.announcementVisible) return null;
  return (
    <div style={{ background:"linear-gradient(90deg,#ea2279,#c01860,#ea2279)", backgroundSize:"200% 100%", animation:"shimmer 4s infinite", textAlign:"center", padding:"8px 14px", fontSize:"0.7rem", fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:"#fff", lineHeight:1.4 }}>
      {content.announcement} &nbsp;
      <a href={content.announcementUrl} target="_blank" rel="noreferrer" style={{ color:C.amber, textDecoration:"none", whiteSpace:"nowrap" }}>{content.announcementLink}</a>
    </div>
  );
}

function Navbar({ currentPage, navigate }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => { const h=()=>setScrolled(window.scrollY>50); window.addEventListener("scroll",h); return()=>window.removeEventListener("scroll",h); },[]);
  useEffect(() => setOpen(false), [currentPage]);

  const links = [["Our Story","story"],["Menu","menu"],["Catering","catering"],["Reviews","reviews"],["Find Us","findus"]];
  const go = p => { navigate(p); setOpen(false); };

  return (
    <>
      <nav style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:isMobile?"0 14px":"0 44px", height:62, background:scrolled||open?"rgba(19,19,23,0.98)":"rgba(19,19,23,0)", borderBottom:scrolled||open?`1px solid ${C.bPink}`:"none", backdropFilter:"blur(8px)", transition:"all 0.3s" }}>
        <button onClick={()=>go("home")} style={{ display:"flex", alignItems:"center", gap:9, background:"none", border:"none", cursor:"pointer", minHeight:44, padding:"4px 0" }}>
          <img src={IMG.logo} alt="Logo" style={{ width:34, height:34, objectFit:"contain", borderRadius:"50%" }} onError={e=>e.target.style.display="none"} />
          <span className="bebas" style={{ fontSize: isMobile?"0.92rem":"1.2rem", letterSpacing:"0.12em", color:C.amber }}>Outside the Masa</span>
        </button>

        {!isMobile && (
          <ul style={{ display:"flex", gap:26, listStyle:"none" }}>
            {links.map(([label,page])=>(
              <li key={page}>
                <button onClick={()=>go(page)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:"0.68rem", fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", color:currentPage===page?C.pinkLight:C.creamDim, position:"relative", padding:"4px 0", minHeight:44 }}>
                  {label}{currentPage===page&&<span style={{ position:"absolute", bottom:-2, left:0, right:0, height:2, background:C.pink, borderRadius:2 }} />}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div style={{ display:"flex", alignItems:"center", gap:9 }}>
          {!isMobile && (
            <button onClick={()=>go("order")} className="bebas" style={{ fontSize:"0.82rem", letterSpacing:"0.14em", color:"#131317", background:currentPage==="order"?C.pinkLight:C.pink, padding:"8px 18px", borderRadius:3, border:"none", cursor:"pointer", minHeight:40, display:"flex", alignItems:"center" }}>
              🛒 Order Online
            </button>
          )}
          {isMobile && (
            <button onClick={()=>setOpen(o=>!o)} style={{ background:"none", border:`1px solid ${C.bPink}`, borderRadius:4, width:42, height:42, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:5, cursor:"pointer", padding:9 }}>
              <span style={{ width:18, height:2, background:open?C.pink:C.cream, transition:"all 0.3s", transform:open?"rotate(45deg) translate(4px,5px)":"none", display:"block" }} />
              <span style={{ width:18, height:2, background:C.cream, opacity:open?0:1, transition:"opacity 0.3s", display:"block" }} />
              <span style={{ width:18, height:2, background:open?C.pink:C.cream, transition:"all 0.3s", transform:open?"rotate(-45deg) translate(4px,-5px)":"none", display:"block" }} />
            </button>
          )}
        </div>
      </nav>

      {isMobile && open && (
        <div style={{ background:"rgba(19,19,23,0.99)", borderBottom:`1px solid ${C.bPink}`, animation:"slideDown 0.22s ease" }}>
          {links.map(([label,page])=>(
            <button key={page} onClick={()=>go(page)} style={{ display:"block", width:"100%", textAlign:"left", background:currentPage===page?"rgba(234,34,121,0.06)":"none", border:"none", borderBottom:`1px solid rgba(255,255,255,0.05)`, padding:"15px 22px", fontFamily:"'Bebas Neue',sans-serif", fontSize:"0.95rem", letterSpacing:"0.18em", textTransform:"uppercase", color:currentPage===page?C.pink:C.cream, cursor:"pointer", minHeight:50 }}>
              {label}
            </button>
          ))}
          <div style={{ padding:"14px 22px 18px" }}>
            <button onClick={()=>go("order")} style={{ display:"block", width:"100%", textAlign:"center", fontFamily:"'Bebas Neue',sans-serif", fontSize:"0.95rem", letterSpacing:"0.18em", color:"#131317", background:C.pink, padding:"13px", borderRadius:3, border:"none", cursor:"pointer" }}>
              🛒 Order Online
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function Header({ currentPage, navigate, content }) {
  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, zIndex:200 }}>
      <Topbar content={content} /><Navbar currentPage={currentPage} navigate={navigate} />
    </div>
  );
}

function Footer({ navigate }) {
  const isMobile = useIsMobile();
  const links = [["Our Story","story"],["Menu","menu"],["Order Online","order"],["Catering","catering"],["Reviews","reviews"],["Find Us","findus"]];
  return (
    <footer style={{ background:"#0d0d10", padding:isMobile?"32px 18px":"44px", borderTop:`1px solid ${C.bPink}` }}>
      <div style={{ maxWidth:1100, margin:"0 auto", display:"flex", flexDirection:isMobile?"column":"row", justifyContent:"space-between", alignItems:isMobile?"flex-start":"center", gap:20, marginBottom:22 }}>
        <div>
          <div className="dance" style={{ fontSize:"1.4rem", color:C.pink }}>Outside the Masa</div>
          <p style={{ fontSize:"0.8rem", color:C.creamDim, marginTop:3 }}>Authentic Birria · Jalisco, México · Indio, CA</p>
        </div>
        <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
          {links.map(([label,page])=>(
            <button key={page} onClick={()=>navigate(page)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:"0.76rem", color:C.creamDim, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:700, minHeight:36 }}>{label}</button>
          ))}
        </div>
      </div>
      <div style={{ maxWidth:1100, margin:"0 auto", paddingTop:14, borderTop:"1px solid rgba(255,255,255,0.05)", display:"flex", flexDirection:isMobile?"column":"row", justifyContent:"space-between", gap:3, fontSize:"0.7rem", color:"rgba(255,255,255,0.22)" }}>
        <span>© 2025 Outside the Masa · Indio, CA</span><span>Birria de Jalisco, México 🌶️</span>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════  HOME  ═══════════════════════════════════════════════ */
function HomePage({ navigate }) {
  useReveal();
  const m = useIsMobile();
  const sp = m ? "52px 18px" : "90px 44px";
  const { content } = useSite();

  return (
    <div className="page-fade">
      {/* HERO */}
      <section style={{ position:"relative", minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", paddingTop:m?96:110 }}>
        <div style={{ position:"absolute", inset:0, display:"grid", gridTemplateColumns:"repeat(2,1fr)", opacity:0.18, filter:"saturate(0.6)" }}>
          {[IMG.tacos,IMG.quesa].map((s,i)=><img key={i} src={s} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />)}
        </div>
        <div style={{ position:"absolute", inset:0, background:"linear-gradient(to bottom,rgba(19,19,23,0.55) 0%,rgba(19,19,23,0.88) 55%,rgba(19,19,23,0.99) 100%)" }} />
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse 80% 60% at 50% 40%,rgba(234,34,121,0.14) 0%,transparent 70%)" }} />

        <div style={{ position:"relative", zIndex:2, width:"100%", maxWidth:1200, padding:m?"0 18px":"0 44px" }}>
          <div style={{ display:"grid", gridTemplateColumns:m?"1fr":"1.1fr 1fr", gap:m?28:56, alignItems:"center" }}>
            <div style={{ textAlign:m?"center":"left", animation:"pageFade 0.9s ease 0.2s both" }}>
              {m && (
                <div style={{ display:"flex", justifyContent:"center", marginBottom:20 }}>
                  <div style={{ width:110, height:110, borderRadius:"50%", border:`2px solid ${C.bPink}`, padding:4, animation:"pulse 4s ease-in-out infinite" }}>
                    <img src={IMG.logo} alt="Logo" style={{ width:"100%", height:"100%", objectFit:"contain", borderRadius:"50%" }} />
                  </div>
                </div>
              )}
              <div style={{ fontSize:"0.66rem", fontWeight:700, letterSpacing:"0.28em", textTransform:"uppercase", color:C.pink, marginBottom:12, display:"flex", alignItems:"center", gap:9, justifyContent:m?"center":"flex-start" }}>
                <span style={{ width:18, height:2, background:C.pink, display:"block" }} />{content.heroEyebrow}
              </div>
              <div className="bebas" style={{ fontSize:m?"clamp(3.8rem,16vw,7rem)":"clamp(4.5rem,11vw,9.5rem)", lineHeight:0.85, WebkitTextStroke:`2px ${C.amber}`, color:"transparent", letterSpacing:"0.04em" }}>OUTSIDE</div>
              <div style={{ display:"flex", alignItems:"baseline", gap:9, justifyContent:m?"center":"flex-start" }}>
                <span className="dance" style={{ fontSize:m?"clamp(2.8rem,11vw,5.5rem)":"clamp(3rem,7.5vw,6.5rem)", color:C.cream, lineHeight:1 }}>the</span>
                <span className="dance" style={{ fontSize:m?"clamp(2.8rem,11vw,5.5rem)":"clamp(3rem,7.5vw,6.5rem)", color:C.pink, lineHeight:1 }}>Masa</span>
              </div>
              <p style={{ fontSize:"0.95rem", lineHeight:1.78, color:C.creamDim, margin:m?"14px auto 18px":"18px 0 22px", maxWidth:420 }}>
                {content.heroSubtitle}
              </p>
              <p style={{ fontSize:"0.68rem", letterSpacing:"0.2em", textTransform:"uppercase", color:C.amber, marginBottom:22 }}>{content.heroAddress}</p>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap", justifyContent:m?"center":"flex-start" }}>
                <BtnPink href={content.orderUrl} style={m?{flex:"1 1 auto"}:{}}>Order Online</BtnPink>
                <BtnOutline onClick={()=>navigate("menu")} style={m?{flex:"1 1 auto"}:{}}>See Our Menu</BtnOutline>
              </div>
            </div>
            {!m && (
              <div style={{ display:"flex", justifyContent:"center", animation:"pageFade 0.9s ease 0.5s both" }}>
                <div style={{ width:"min(320px,65vw)", height:"min(320px,65vw)", borderRadius:"50%", border:`3px solid ${C.bPink}`, padding:5, animation:"pulse 4s ease-in-out infinite" }}>
                  <img src={IMG.logo} alt="Logo" style={{ width:"100%", height:"100%", objectFit:"contain", borderRadius:"50%" }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PHOTO STRIP — live gallery images */}
      <div style={{ display:"grid", gridTemplateColumns:m?"1fr 1fr":"2fr 1fr 1fr", gridTemplateRows:m?"150px 150px":"270px 270px", gap:3 }}>
        {(content.galleryImages.length > 0 ? content.galleryImages : [IMG.master,IMG.tacos,IMG.broth,IMG.quesa,IMG.fresh]).slice(0,m?4:5).map((img,i)=>(
          <div key={i} style={{ overflow:"hidden", gridRow:(!m&&i===0)?"span 2":undefined }}>
            <img src={img} alt="" loading="lazy" style={{ width:"100%", height:"100%", objectFit:"cover", filter:"brightness(0.75)", display:"block" }} />
          </div>
        ))}
      </div>

      {/* STORY PREVIEW */}
      <section style={{ background:C.bg, padding:sp }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns:m?"1fr":"1fr 1fr", gap:m?24:72, alignItems:"center" }}>
          <div className={m?"reveal":"reveal-l"} style={{ borderRadius:4, overflow:"hidden", border:`2px solid ${C.bPink}` }}>
            <img src={IMG.master} alt="Master" style={{ width:"100%", height:m?230:380, objectFit:"cover", display:"block" }} />
          </div>
          <div className={m?"reveal":"reveal-r"}>
            <SectionLabel>Our Story</SectionLabel>
            <SectionTitle bebas="A Taste of Jalisco" script="in Every Bite" />
            <Divider />
            <p style={{ fontSize:"0.96rem", lineHeight:1.8, color:C.creamDim, marginBottom:12 }}>{content.storyP1}</p>
            <p style={{ fontSize:"0.96rem", lineHeight:1.8, color:C.creamDim, marginBottom:26 }}>{content.storyP2}</p>
            <BtnPink onClick={()=>navigate("story")} style={m?{width:"100%",justifyContent:"center"}:{}}>Read Our Story</BtnPink>
          </div>
        </div>
      </section>

      {/* QUALITY */}
      <div style={{ background:C.bg2, borderTop:`1px solid ${C.bPink}`, borderBottom:`1px solid ${C.bPink}`, padding:m?"36px 18px":"56px 44px" }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns:m?"1fr":"repeat(3,1fr)", gap:m?24:44 }}>
          {[["🌶️","Authentic Jalisco Recipe","Traditional family recipe slow-cooked with dried chiles and agave leaves for generations."],["🔥","Slow Cooked to Perfection","We take pride in quality, starting with the finest ingredients. Zero shortcuts."],["🏆","Award-Winning","Recognized by the Coachella Valley for exceptional flavor and authenticity. Five stars everywhere."]].map(([icon,title,text],i)=>(
            <div key={i} className="reveal" style={{ textAlign:"center", transitionDelay:`${i*0.1}s` }}>
              <div style={{ width:52, height:52, borderRadius:"50%", border:`2px solid ${C.bAmb}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.5rem", margin:"0 auto 10px", background:"rgba(236,176,81,0.07)" }}>{icon}</div>
              <h3 className="bebas" style={{ fontSize:"0.95rem", letterSpacing:"0.14em", color:C.amber, marginBottom:6 }}>{title}</h3>
              <p style={{ fontSize:"0.88rem", color:C.creamDim, lineHeight:1.7 }}>{text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* MENU PREVIEW */}
      <section style={{ background:C.bg2, padding:sp }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <div className="reveal" style={{ textAlign:"center", marginBottom:28 }}>
            <SectionLabel center>What We Serve</SectionLabel>
            <SectionTitle bebas="The Menu" center />
          </div>
          <div style={{ display:"grid", gridTemplateColumns:m?"1fr 1fr":"repeat(4,1fr)", gap:3 }}>
            {[[IMG.tacos,"Birria Tacos","Fan Favorite"],[IMG.quesa,"Quesabirria","Best Seller"],[IMG.broth,"House Consommé","Must Try"],[IMG.bowl,"Birria Bowl","Signature"]].map(([img,name,badge],i)=>(
              <div key={i} className="reveal" style={{ background:C.bg, overflow:"hidden", transitionDelay:`${i*0.07}s` }}>
                <img src={img} alt={name} loading="lazy" style={{ width:"100%", height:m?130:190, objectFit:"cover", display:"block" }} />
                <div style={{ padding:m?"10px 12px":"16px" }}>
                  <div className="dance" style={{ fontSize:m?"1.05rem":"1.35rem", color:C.cream, marginBottom:4 }}>{name}</div>
                  <span style={{ display:"inline-block", fontSize:"0.56rem", fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", color:"#fff", background:C.pink, padding:"2px 7px", borderRadius:2 }}>{badge}</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign:"center", marginTop:24 }}>
            <BtnPink onClick={()=>navigate("menu")} style={m?{width:"100%",justifyContent:"center"}:{}}>View Full Menu</BtnPink>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div style={{ background:`linear-gradient(135deg,rgba(234,34,121,0.09),rgba(236,176,81,0.05))`, border:`1px solid ${C.bPink}`, margin:m?"0 14px":"0 44px", borderRadius:6, padding:m?"30px 18px":"52px 44px", textAlign:"center" }}>
        <div className="dance" style={{ fontSize:"clamp(1.7rem,6vw,3.4rem)", color:C.cream, marginBottom:8 }}>Book Us for Your Next Event</div>
        <p style={{ fontSize:"0.93rem", color:C.creamDim, marginBottom:20, maxWidth:440, margin:"0 auto 20px" }}>Weddings, corporates, festivals, private parties.</p>
        <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" }}>
          <BtnPink onClick={()=>navigate("catering")} style={m?{flex:"1 1 auto"}:{}}>Explore Catering</BtnPink>
          <BtnOutline onClick={()=>navigate("reviews")} style={m?{flex:"1 1 auto"}:{}}>See Reviews</BtnOutline>
        </div>
      </div>
      <div style={{ height:36, background:C.bg }} />
      <Footer navigate={navigate} />
    </div>
  );
}

/* ═══════════════════════════════════════════════  STORY  ═══════════════════════════════════════════════ */
function StoryPage({ navigate }) {
  useReveal();
  const m = useIsMobile();
  const sp = m?"50px 18px":"88px 44px";
  const { content } = useSite();

  const values=[["🌶️","Authenticity","Every batch of birria is made the traditional Jalisco way — slow-cooked with dried chiles, agave leaves, and decades of family knowledge."],["👨‍👩‍👧‍👦","Familia","Every customer who walks through our door becomes part of our extended familia. We feed you like we feed our own."],["🔥","Quality First","We start with the finest, freshest ingredients. Our house-made consommé, hand-pressed tortillas, and fresh garnishes crafted daily."],["🏆","Community Pride","We are proud to serve the Coachella Valley and invest in every event and celebration."],["🤝","Service with Heart","Our catering team shows up with the same passion as if it were our own family's party. We deliver an experience."],["🌮","Innovation","While we honor tradition, we embrace creativity — from our quesabirria to rotating agua fresca flavors."]];
  const tl=[["The Beginning","Jalisco Roots","Our family recipe originates from Jalisco — birthplace of birria. Generations of knowledge passed down with pride and love.",C.pink],["Food Truck Era","Taking It to the Streets","We launched our birria food truck in the Coachella Valley. Word spread fast. Lines grew. The community fell in love.",C.amber],["Award-Winning","Community Recognition","Five-star reviews poured in on Yelp, Google, and Facebook. We were recognized for outstanding authenticity and flavor.",C.pinkLight],["Today","A Restaurant in Indio","We graduated from food truck to full restaurant at 43-905 Clinton St, Suite 5, Indio, CA. Same soul. Bigger table.",C.amber]];

  return (
    <div className="page-fade" style={{ paddingTop:m?94:110 }}>
      <PageHero eyebrow="The People Behind the Masa" title="OUR STORY" script="familia · fuego · sabor" subtitle="From a family kitchen in Jalisco to the streets of Indio — this is how the masa got outside." imgs={[IMG.master,IMG.prep,IMG.truck]} />

      <section style={{ background:C.bg, padding:sp }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns:m?"1fr":"1fr 1fr", gap:m?22:72, alignItems:"center" }}>
          <div className={m?"reveal":"reveal-l"} style={{ borderRadius:4, overflow:"hidden", border:`2px solid ${C.bPink}` }}>
            <img src={IMG.master} alt="Master" style={{ width:"100%", height:m?240:400, objectFit:"cover", display:"block" }} />
          </div>
          <div className={m?"reveal":"reveal-r"}>
            <SectionLabel>Where It Began</SectionLabel>
            <SectionTitle bebas="A Family Recipe" script="Born in Jalisco" />
            <Divider />
            <p style={{ fontSize:"0.96rem", lineHeight:1.8, color:C.creamDim, marginBottom:12 }}>{content.storyP1}</p>
            <p style={{ fontSize:"0.96rem", lineHeight:1.8, color:C.creamDim, marginBottom:12 }}>The foundation lies in slow-cooked meat, seasoned with aromatic spices and agave leaves.</p>
            <p style={{ fontSize:"0.96rem", lineHeight:1.8, color:C.creamDim }}>{content.storyP2}</p>
          </div>
        </div>
      </section>

      <section style={{ background:C.bg2, padding:sp }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div className="reveal" style={{ marginBottom:30 }}>
            <SectionLabel>How We Got Here</SectionLabel>
            <SectionTitle bebas="Our Journey" />
          </div>
          <div style={{ position:"relative", paddingLeft:32 }}>
            <div style={{ position:"absolute", left:8, top:0, bottom:0, width:2, background:`linear-gradient(to bottom,${C.pink},${C.amber},transparent)` }} />
            {tl.map(([year,title,text,color],i)=>(
              <div key={i} className="reveal" style={{ position:"relative", marginBottom:32, transitionDelay:`${i*0.1}s` }}>
                <div style={{ position:"absolute", left:-27, top:5, width:13, height:13, borderRadius:"50%", background:color, border:`3px solid ${C.bg2}`, boxShadow:`0 0 0 2px ${color}` }} />
                <div className="bebas" style={{ fontSize:"0.75rem", letterSpacing:"0.2em", color:C.amber, marginBottom:3 }}>{year}</div>
                <div className="dance" style={{ fontSize:"1.35rem", color:C.cream, marginBottom:5 }}>{title}</div>
                <p style={{ fontSize:"0.9rem", color:C.creamDim, lineHeight:1.7 }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div style={{ height:m?220:320, overflow:"hidden", position:"relative" }}>
        <img src={IMG.platter} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", filter:"brightness(0.33)", display:"block" }} />
        <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center", padding:18 }}>
          <div className="dance" style={{ fontSize:"clamp(1.9rem,7vw,4.5rem)", color:C.cream, marginBottom:8 }}>"Same soul, bigger table."</div>
          <p style={{ fontSize:"0.92rem", color:C.creamDim }}>From food truck to restaurant — the fire never stopped burning.</p>
        </div>
      </div>

      <section style={{ background:C.bg, padding:sp }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div className="reveal" style={{ textAlign:"center", marginBottom:28 }}>
            <SectionLabel center>What Drives Us</SectionLabel>
            <SectionTitle bebas="Our Values" center />
          </div>
          <div style={{ display:"grid", gridTemplateColumns:m?"1fr 1fr":"repeat(3,1fr)", gap:m?10:16 }}>
            {values.map(([icon,title,text],i)=>(
              <div key={i} className="reveal" style={{ background:C.bg2, border:`1px solid ${C.bPink}`, borderRadius:4, padding:m?"16px 12px":"26px 20px", textAlign:"center", transitionDelay:`${i*0.06}s` }}>
                <div style={{ fontSize:m?"1.7rem":"2.2rem", marginBottom:9 }}>{icon}</div>
                <h3 className="bebas" style={{ fontSize:m?"0.82rem":"0.98rem", letterSpacing:"0.12em", color:C.amber, marginBottom:7 }}>{title}</h3>
                <p style={{ fontSize:m?"0.78rem":"0.88rem", color:C.creamDim, lineHeight:1.65 }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div style={{ background:C.bg3, borderTop:`1px solid ${C.bPink}`, padding:m?"40px 18px":"64px 44px", textAlign:"center" }}>
        <div className="dance" style={{ fontSize:"clamp(1.7rem,6vw,3.4rem)", color:C.cream, marginBottom:8 }}>Ready to taste the story?</div>
        <p style={{ fontSize:"0.93rem", color:C.creamDim, marginBottom:20 }}>Come visit us in Indio — or let us bring the birria to your event.</p>
        <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" }}>
          <BtnPink onClick={()=>navigate("menu")} style={m?{flex:"1 1 auto"}:{}}>See Our Menu</BtnPink>
          <BtnOutline onClick={()=>navigate("catering")} style={m?{flex:"1 1 auto"}:{}}>Book Catering</BtnOutline>
        </div>
      </div>
      <Footer navigate={navigate} />
    </div>
  );
}

/* ═══════════════════════════════════════════════  MENU  ═══════════════════════════════════════════════ */

function MenuPage({ navigate }) {
  useReveal();
  const m = useIsMobile();
  const { menuItems } = useSite();
  const [cat,setCat]=useState("all");
  const [q,setQ]=useState("");
  const cats={birria:"Birria Specialties 🌶️",tacos:"Tacos & Quesadillas 🌮",sides:"Sides & Extras 🍟",drinks:"Bebidas & Cerveza 🥤",extras:"Extras"};
  const catOrder=["birria","tacos","sides","drinks","extras"];
  const filtered=menuItems.filter(i=>(cat==="all"||i.cat===cat)&&(!q||i.name.toLowerCase().includes(q)||i.desc.toLowerCase().includes(q)));
  const grouped={};
  filtered.forEach(i=>{if(!grouped[i.cat])grouped[i.cat]=[];grouped[i.cat].push(i);});
  const tabs=[{id:"all",label:"All"},{id:"birria",label:"Birria"},{id:"sides",label:"Sides"},{id:"drinks",label:"Drinks"},{id:"extras",label:"Extras"}];

  return (
    <div className="page-fade" style={{ paddingTop:m?94:110 }}>
      <PageHero eyebrow="Crafted from a Jalisco Family Recipe" title="THE MENU" subtitle="Slow-cooked, made-to-order, and built for the love of birria." imgs={[IMG.tacos,IMG.quesa,IMG.broth]} />

      <section style={{ padding:m?"28px 16px":"60px 44px", maxWidth:1200, margin:"0 auto" }}>
        <div className="reveal" style={{ background:`linear-gradient(135deg,rgba(234,34,121,0.09),rgba(236,176,81,0.05))`, border:`1px solid ${C.bPink}`, borderRadius:6, padding:m?"16px":"22px 30px", marginBottom:24, display:"flex", flexDirection:m?"column":"row", alignItems:m?"flex-start":"center", justifyContent:"space-between", gap:10 }}>
          <div>
            <div className="dance" style={{ fontSize:m?"1.35rem":"1.75rem", color:C.cream, marginBottom:4 }}>🏆 Today's Must-Try: Los Chilaquillers</div>
            <p style={{ fontSize:"0.88rem", color:C.creamDim, lineHeight:1.6 }}>Red or green salsa, topped with birria, beans, queso fresco, Mexican crema, onions, and cilantro. House-made tortilla chips.</p>
          </div>
          <span className="bebas" style={{ flexShrink:0, background:C.pink, color:"#fff", fontSize:"0.76rem", letterSpacing:"0.14em", padding:"5px 11px", borderRadius:2 }}>★ Best Seller</span>
        </div>

        <div style={{ marginBottom:18 }}>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="🔍 Search the menu..." style={{ background:C.bg2, border:`1px solid ${C.bPink}`, color:C.cream, padding:"11px 14px", borderRadius:3, fontFamily:"'Nunito',sans-serif", outline:"none", width:"100%", marginBottom:10 }} />
          <div style={{ display:"flex", gap:4, overflowX:"auto", paddingBottom:3, WebkitOverflowScrolling:"touch" }}>
            {tabs.map(({id,label})=>(
              <button key={id} onClick={()=>setCat(id)} className="bebas" style={{ flexShrink:0, fontSize:"0.8rem", letterSpacing:"0.11em", padding:"8px 16px", border:`2px solid`, borderColor:cat===id?C.pink:C.bPink, background:cat===id?C.pink:"transparent", color:cat===id?"#fff":C.creamDim, cursor:"pointer", borderRadius:3, minHeight:38, whiteSpace:"nowrap" }}>{label}</button>
            ))}
          </div>
        </div>

        {filtered.length===0?(
          <div style={{ textAlign:"center", padding:"36px 0", color:C.creamDim }}>No items found for "<strong style={{ color:C.pink }}>{q}</strong>"</div>
        ):(
          catOrder.filter(c=>grouped[c]).map(c=>(
            <div key={c}>
              {cat==="all"&&<div style={{ display:"flex", alignItems:"center", gap:10, margin:"24px 0 12px" }}>
                <span className="bebas" style={{ fontSize:m?"1rem":"1.25rem", letterSpacing:"0.11em", color:C.amber, whiteSpace:"nowrap" }}>{cats[c]}</span>
                <div style={{ flex:1, height:1, background:C.bAmb }} />
                <span style={{ fontSize:"0.68rem", color:C.creamDim, whiteSpace:"nowrap" }}>{grouped[c].length} items</span>
              </div>}
              <div style={{ display:"grid", gridTemplateColumns:m?"1fr 1fr":"repeat(auto-fill,minmax(250px,1fr))", gap:3, marginBottom:20 }}>
                {grouped[c].map(item=>(
                  <div key={item.id} style={{ background:C.bg2, overflow:"hidden", display:"flex", flexDirection:"column" }}>
                    {item.img&&<img src={item.img} alt={item.name} loading="lazy" style={{ width:"100%", height:m?120:185, objectFit:"cover", display:"block" }} />}
                    <div style={{ padding:m?"10px":"15px", flex:1, display:"flex", flexDirection:"column", gap:6 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:8 }}>
                        <div className="dance" style={{ fontSize:m?"1.05rem":"1.3rem", color:C.cream }}>{item.name}</div>
                        {item.price&&<span className="bebas" style={{ fontSize:m?"0.9rem":"1.05rem", color:C.amber, letterSpacing:"0.05em", flexShrink:0 }}>{item.price}</span>}
                      </div>
                      <p style={{ fontSize:m?"0.77rem":"0.88rem", color:C.creamDim, lineHeight:1.55, flex:1 }}>{item.desc}</p>
                      {item.badge&&<span style={{ display:"inline-block", fontSize:"0.56rem", fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", color:item.ba?C.bg:"#fff", background:item.ba?C.amberDim:C.pink, padding:"2px 6px", borderRadius:2, alignSelf:"flex-start" }}>{item.badge}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </section>

      <div style={{ background:C.bg3, borderTop:`1px solid ${C.bPink}`, padding:m?"36px 18px":"56px 44px", textAlign:"center" }}>
        <div className="dance" style={{ fontSize:"clamp(1.7rem,6vw,3rem)", color:C.cream, marginBottom:7 }}>Ready to Order?</div>
        <p style={{ fontSize:"0.93rem", color:C.creamDim, marginBottom:18 }}>Order ahead for pickup through our Square store.</p>
        <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" }}>
          <BtnPink href="https://outside-the-masa.square.site/" style={m?{flex:"1 1 auto"}:{}}>Order Online</BtnPink>
          <BtnOutline onClick={()=>navigate("catering")} style={m?{flex:"1 1 auto"}:{}}>Book Catering</BtnOutline>
        </div>
      </div>
      <Footer navigate={navigate} />
    </div>
  );
}

/* ═══════════════════════════════════════════════  CATERING  ═══════════════════════════════════════════════ */
function CateringPage({ navigate }) {
  useReveal();
  const m = useIsMobile();
  const sp = m?"48px 18px":"86px 44px";
  const [form,setForm]=useState({firstName:"",lastName:"",email:"",phone:"",date:"",guests:"",service:"",location:"",notes:""});
  const [submitted,setSubmitted]=useState(false);
  const [sending,setSending]=useState(false);
  const [openFaq,setOpenFaq]=useState(null);

  const go = async () => {
    if(!form.firstName||!form.lastName||!form.email||!form.phone||!form.date||!form.guests||!form.service||!form.location){alert("Please fill in all required fields.");return;}
    setSending(true);
    try {
      await fetch("https://formsubmit.co/ajax/mexbarajas@gmail.com", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          _subject:   "\uD83C\uDF36\uFE0F Catering Request \u2014 " + form.firstName + " " + form.lastName,
          _cc:        "outsidethemasa@gmail.com",
          _replyto:   form.email,
          _template:  "table",
          Name:       form.firstName + " " + form.lastName,
          Email:      form.email,
          Phone:      form.phone || "\u2014",
          "Event Date": form.date || "\u2014",
          Guests:     form.guests || "\u2014",
          Service:    form.service || "\u2014",
          Location:   form.location || "\u2014",
          Notes:      form.notes || "\u2014",
        }),
      });
      setSubmitted(true);
    } catch {
      alert("Something went wrong. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  const pkgs=[
    {num:"01",name:"Food Truck Service",img:IMG.truck,badge:"Food Truck",desc:"Our 18' fully equipped food trailer rolls to your venue. Made-to-order, fresh, and theatrical.",features:["Full 18' food trailer","Fully staffed crew","Made-to-order on-site","Setup & breakdown included"],color:C.pink},
    {num:"02",name:"Buffet Style",img:IMG.master,badge:"Most Popular",desc:"Full buffet line in beautiful chafing dishes, served by our staff. Elegant and efficient.",features:["Professional chafing setup","Staff-served buffet","Full utensils & plating","Customizable menu"],color:C.amber,featured:true},
    {num:"03",name:"Drop-Off Service",img:IMG.prep,badge:"Drop-Off",desc:"We deliver trays of our food to your venue, ready to serve. Convenient and cost-effective.",features:["Chafing-ready trays","Labelled & organized","Heating instructions","On-time delivery"],color:C.pink},
  ];
  const faqs=[["What's the minimum order?","All three packages require a minimum of 50 servings. For smaller groups, visit us at the restaurant or order online."],["How far in advance should I book?","We recommend 2–4 weeks in advance. For festivals (200+ people), 4–6 weeks."],["Do you serve outside the Coachella Valley?","Our primary area is the Coachella Valley, but we travel for larger events. Travel fees may apply."],["Can I customize the menu?","Our full menu includes Birria, Asada, Pollo, Tamales, Tacos, Quesadillas, Rice & Beans, Agua Fresca, Guacamole Bar, Salsa Bar, Nacho Bar, and Ice Cream Carts."],["Do you cater weddings?","Yes — birria at a wedding is unforgettable! Our food truck is especially popular for outdoor weddings."],["What's included in the buffet?","Food, chafing dishes, staffed service, utensils, plating, and napkins. We handle everything."]];


  return (
    <div className="page-fade" style={{ paddingTop:m?94:110 }}>
      <PageHero eyebrow="Weddings · Corporates · Festivals · Private Events" title="CATERING" script="We bring the birria to you." subtitle="Authentic birria, made fresh on-site or delivered — for events of 50 to 5,000." imgs={[IMG.truck,IMG.prep,IMG.master]} />

      {/* PACKAGES */}
      <section style={{ background:C.bg, padding:sp }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div className="reveal" style={{ textAlign:"center", marginBottom:28 }}>
            <SectionLabel center>Choose Your Experience</SectionLabel>
            <SectionTitle bebas="Catering Packages" center />
            <p style={{ fontSize:"0.91rem", color:C.creamDim, marginTop:7 }}>All packages include full menu, setup, and staffed service.</p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:m?"1fr":"repeat(3,1fr)", gap:m?14:18 }}>
            {pkgs.map(({num,name,img,badge,desc,features,featured},i)=>(
              <div key={i} className="reveal" style={{ background:C.bg2, border:`2px solid`, borderColor:featured?C.amber:C.bPink, borderRadius:6, overflow:"hidden", display:"flex", flexDirection:"column", transitionDelay:`${i*0.1}s` }}>
                <div style={{ height:m?160:195, overflow:"hidden" }}>
                  <img src={img} alt={name} loading="lazy" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
                </div>
                <div style={{ background:featured?C.amberDim:C.pink, color:featured?C.bg:"#fff", fontFamily:"'Bebas Neue',sans-serif", fontSize:"0.7rem", letterSpacing:"0.16em", padding:"5px 0", textAlign:"center" }}>{badge}</div>
                <div style={{ padding:m?"16px":"22px", flex:1, display:"flex", flexDirection:"column", gap:9 }}>
                  <div className="bebas" style={{ fontSize:"2.2rem", color:featured?C.amber:C.pink, opacity:0.2, lineHeight:1 }}>{num}</div>
                  <div className="dance" style={{ fontSize:m?"1.4rem":"1.6rem", color:C.cream }}>{name}</div>
                  <p style={{ fontSize:"0.88rem", color:C.creamDim, lineHeight:1.65, flex:1 }}>{desc}</p>
                  <ul style={{ listStyle:"none", display:"flex", flexDirection:"column", gap:5 }}>
                    {features.map((f,fi)=><li key={fi} style={{ fontSize:"0.85rem", color:C.creamDim, display:"flex", gap:7 }}><span style={{ color:C.pink, fontWeight:700 }}>✓</span>{f}</li>)}
                  </ul>
                  <div style={{ fontSize:"0.64rem", fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", color:featured?C.amber:C.pink }}>📋 Minimum 50 servings</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MENU CHIPS */}
      <section style={{ background:C.bg2, padding:`${m?"40px":"68px"} ${m?"18px":"44px"} 0`, overflow:"hidden" }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div className="reveal">
            <SectionLabel>What We Serve</SectionLabel>
            <SectionTitle bebas="Full Catering Menu" />
            <p style={{ fontSize:"0.94rem", color:C.creamDim, lineHeight:1.8, maxWidth:580, margin:"12px 0 14px" }}>Mix and match from our full menu to build the perfect spread for your event.</p>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:14 }}>
              {["Birria de Res","Birria de Pollo","Carne Asada","Tamales","Birria Tacos","Quesabirria","Quesadillas","Rice & Beans","Agua Fresca","Guacamole Bar","Salsa Bar","Nacho Bar","Ice Cream Cart","Consommé Station","Chips & Dips"].map(item=>(
                <span key={item} style={{ background:"rgba(236,176,81,0.1)", border:`1px solid ${C.bAmb}`, color:C.amber, fontSize:m?"0.68rem":"0.74rem", fontWeight:700, letterSpacing:"0.08em", padding:m?"4px 9px":"4px 11px", borderRadius:20, textTransform:"uppercase" }}>{item}</span>
              ))}
            </div>
          </div>
          <div className="reveal" style={{ display:"grid", gridTemplateColumns:m?"1fr 1fr":"2fr 1fr 1fr", height:m?160:250, gap:3, overflow:"hidden" }}>
            {[IMG.tacos,IMG.broth,IMG.quesa].map((src,i)=>(
              <div key={i} style={{ overflow:"hidden", height:"100%" }}>
                <img src={src} alt="" loading="lazy" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block", filter:"brightness(0.8)" }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INQUIRY */}
      <section style={{ background:C.bg, padding:sp }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns:m?"1fr":"1fr 1.15fr", gap:m?24:72, alignItems:"start" }}>
          <div className="reveal">
            <SectionLabel>Get a Quote</SectionLabel>
            <SectionTitle bebas="Tell Us About Your Event" />
            <Divider />
            <p style={{ fontSize:"0.94rem", color:C.creamDim, lineHeight:1.8, marginBottom:12 }}>We typically respond within 24 hours with a custom quote based on your needs.</p>
            <div style={{ background:"rgba(236,176,81,0.07)", border:`1px solid ${C.bAmb}`, borderRadius:4, padding:14 }}>
              <p className="bebas" style={{ fontSize:"0.72rem", letterSpacing:"0.18em", color:C.amber, marginBottom:7 }}>Quick Facts</p>
              <p style={{ fontSize:"0.84rem", color:C.creamDim, lineHeight:2 }}>📋 Minimum: 50 servings<br/>🚚 Service: Coachella Valley &amp; surrounding<br/>🎪 Festivals: Yes, we do those<br/>📅 Preferably book 2–4 weeks in advance</p>
            </div>
          </div>
          <div className="reveal">
            {submitted?(
              <div style={{ background:"rgba(76,175,80,0.1)", border:"1px solid rgba(76,175,80,0.4)", borderRadius:6, padding:32, textAlign:"center" }}>
                <div style={{ fontSize:"2.2rem", marginBottom:10 }}>✅</div>
                <div className="dance" style={{ fontSize:"1.7rem", color:C.cream, marginBottom:5 }}>Request Received!</div>
                <p style={{ color:C.creamDim, marginBottom:14 }}>¡Gracias! We'll be in touch within 24 hours. 🌶️</p>
                <BtnPink onClick={()=>setSubmitted(false)} style={{ width:"100%", justifyContent:"center" }}>Send Another</BtnPink>
              </div>
            ):(
              <div style={{ background:C.bg2, border:`1px solid ${C.bPink}`, borderRadius:6, padding:m?"20px 16px":"28px" }}>
                <div className="dance" style={{ fontSize:"1.75rem", color:C.cream, marginBottom:3 }}>Request a Quote</div>
                <p style={{ fontSize:"0.86rem", color:C.creamDim, marginBottom:18 }}>Fill out the form and we'll get back to you within 24 hours.</p>
                {[
                  [["First Name *","firstName","text","Maria"],["Last Name *","lastName","text","Garcia"]],
                  [["Email *","email","email","you@example.com"],["Phone *","phone","tel","(760) 555-0000"]],
                  [["Event Date *","date","date",""],["Guests *","guests","number","100"]],
                  [["Event Location *","location","text","Indio, CA"]],
                ].map((row,ri)=>(
                  <div key={ri} style={{ display:"grid", gridTemplateColumns:row.length===2&&!m?"1fr 1fr":"1fr", gap:10, marginBottom:10 }}>
                    {row.map(([label,key,type,ph])=>(
                      <div key={key} style={{ display:"flex", flexDirection:"column", gap:4 }}>
                        <label style={{ fontSize:"0.58rem", fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", color:C.pink }}>{label}</label>
                        <input type={type} placeholder={ph} value={form[key]} onChange={e=>setForm({...form,[key]:e.target.value})}
                          style={{ background:"rgba(255,255,255,0.04)", border:`1px solid ${C.bPink}`, color:C.cream, padding:"12px 12px", borderRadius:3, fontFamily:"'Nunito',sans-serif", outline:"none", width:"100%" }} />
                      </div>
                    ))}
                  </div>
                ))}
                {[["Service Type *","service","select"],["Notes","notes","textarea"]].map(([label,key,type])=>(
                  <div key={key} style={{ display:"flex", flexDirection:"column", gap:4, marginBottom:10 }}>
                    <label style={{ fontSize:"0.58rem", fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", color:C.pink }}>{label}</label>
                    {type==="select"?(
                      <select value={form[key]} onChange={e=>setForm({...form,[key]:e.target.value})} style={{ background:C.bg, border:`1px solid ${C.bPink}`, color:C.cream, padding:"12px 12px", borderRadius:3, fontFamily:"'Nunito',sans-serif", outline:"none" }}>
                        <option value="">Select a package...</option>
                        <option>Food Truck Service</option><option>Buffet Style Service</option>
                        <option>Drop-Off Service</option><option>Festival / Large Event</option>
                        <option>Not sure — help me decide</option>
                      </select>
                    ):(
                      <textarea value={form[key]} onChange={e=>setForm({...form,[key]:e.target.value})} placeholder="Event type, timing, special requests..." rows={3}
                        style={{ background:"rgba(255,255,255,0.04)", border:`1px solid ${C.bPink}`, color:C.cream, padding:"12px 12px", borderRadius:3, fontFamily:"'Nunito',sans-serif", outline:"none", resize:"vertical" }} />
                    )}
                  </div>
                ))}
                <BtnPink onClick={go} disabled={sending} style={{ width:"100%", justifyContent:"center", marginTop:6, opacity:sending?0.7:1 }}>{sending?"Sending...":"Send My Request 🌶️"}</BtnPink>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ background:C.bg2, padding:sp }}>
        <div style={{ maxWidth:860, margin:"0 auto" }}>
          <div className="reveal" style={{ textAlign:"center", marginBottom:24 }}>
            <SectionLabel center>Common Questions</SectionLabel>
            <SectionTitle bebas="FAQ" center />
          </div>
          <div className="reveal" style={{ display:"flex", flexDirection:"column", gap:3 }}>
            {faqs.map(([q,a],i)=>(
              <div key={i} style={{ background:C.bg, border:`1px solid ${C.bPink}`, borderRadius:3 }}>
                <div onClick={()=>setOpenFaq(openFaq===i?null:i)} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:m?"14px 16px":"17px 20px", cursor:"pointer" }}>
                  <span style={{ fontWeight:700, fontSize:m?"0.88rem":"0.93rem", paddingRight:10 }}>{q}</span>
                  <span style={{ color:C.pink, fontSize:"1.25rem", transform:openFaq===i?"rotate(45deg)":"none", transition:"transform 0.3s", flexShrink:0 }}>+</span>
                </div>
                {openFaq===i&&<div style={{ padding:m?"0 16px 14px":"0 20px 16px", fontSize:"0.9rem", color:C.creamDim, lineHeight:1.75, borderTop:`1px solid ${C.bPink}` }}>{a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer navigate={navigate} />
    </div>
  );
}

/* ═══════════════════════════════════════════════  REVIEWS  ═══════════════════════════════════════════════ */
const REVIEWS_DATA=[
  {platform:"yelp",stars:5,text:"The birria tacos are absolutely incredible. The consommé is rich and the meat melts in your mouth. Best birria in the Coachella Valley, hands down.",name:"Maria T.",date:"March 2025",dish:"Birria Tacos"},
  {platform:"google",stars:5,text:"We hired Outside the Masa for our company event — every single guest raved. Authentic, delicious, and so professional.",name:"Carlos M.",date:"February 2025",dish:"Catering"},
  {platform:"facebook",stars:5,text:"The quesabirria is a religious experience. That cheese pull dipped in consommé — I dream about it. We go every week now.",name:"Jessica R.",date:"March 2025",dish:"Quesabirria"},
  {platform:"yelp",stars:5,text:"I've had birria in many places including Jalisco itself. This is legitimately the real deal. The consommé alone is worth the drive.",name:"Roberto A.",date:"January 2025",dish:"Consommé"},
  {platform:"google",stars:5,text:"Five stars! We ordered the food truck for my daughter's quinceañera — absolutely perfect. Professional, on time, exceptional food.",name:"Sandra P.",date:"February 2025",dish:"Catering"},
  {platform:"facebook",stars:5,text:"Outside the Masa is a gem. The food truck always has a line and now the restaurant is packed — service is fast and everything is worth it.",name:"David H.",date:"March 2025",dish:"Birria Bowl"},
  {platform:"catering",stars:5,text:"Booked the buffet for our corporate luncheon — 120 people. They handled everything perfectly. Setup, service, cleanup. Incredible food.",name:"Veronica T.",date:"January 2025",dish:"Buffet Catering"},
  {platform:"yelp",stars:5,text:"The salsa bar is incredible. They give multiple house-made salsas and the habanero one is 🔥.",name:"Ana C.",date:"March 2025",dish:"Salsa Bar"},
  {platform:"google",stars:5,text:"Best birria outside of Mexico. Period. Hand-pressed tortillas, deep smoky broth, melted cheese. I order pickup at least once a week.",name:"Miguel R.",date:"February 2025",dish:"Birria Tacos"},
];

function ReviewsPage({ navigate }) {
  useReveal();
  const m = useIsMobile();
  const sp = m?"44px 18px":"76px 44px";
  const [filter,setFilter]=useState("all");
  const platforms=[{id:"all",label:"All"},{id:"yelp",label:"Yelp"},{id:"google",label:"Google"},{id:"facebook",label:"Facebook"},{id:"catering",label:"Catering"}];
  const filtered=filter==="all"?REVIEWS_DATA:REVIEWS_DATA.filter(r=>r.platform===filter);

  return (
    <div className="page-fade" style={{ paddingTop:m?94:110 }}>
      <PageHero eyebrow="What the Community Is Saying" title="REVIEWS" subtitle="Don't take our word for it. Here's what the people of the Coachella Valley say." />

      {/* SCORE */}
      <div style={{ background:C.bg2, borderBottom:`1px solid ${C.bPink}`, padding:m?"24px 18px":"38px 44px" }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"grid", gridTemplateColumns:m?"1fr 1fr 1fr":"auto 1fr auto auto auto", gap:m?"16px":"36px", alignItems:"center" }}>
          <div style={{ textAlign:"center" }}>
            <div className="bebas" style={{ fontSize:m?"3.5rem":"5rem", lineHeight:1, color:C.amber }}>4.9</div>
            <div style={{ fontSize:m?"0.9rem":"1.1rem", color:C.amber, letterSpacing:2 }}>★★★★★</div>
            <div style={{ fontSize:"0.64rem", color:C.creamDim, textTransform:"uppercase", letterSpacing:"0.1em", marginTop:2 }}>Overall</div>
          </div>
          {!m&&<div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            {[["5","92%",C.pink],["4","6%",C.amber],["3","2%","#888"],["2","0%","#555"],["1","0%","#555"]].map(([star,pct,color])=>(
              <div key={star} style={{ display:"flex", alignItems:"center", gap:9, fontSize:"0.77rem" }}>
                <span style={{ width:11, color:C.creamDim, textAlign:"right" }}>{star}</span>
                <div style={{ flex:1, height:5, background:"rgba(255,255,255,0.08)", borderRadius:3, overflow:"hidden" }}>
                  <div style={{ width:pct, height:"100%", background:color, borderRadius:3 }} />
                </div>
                <span style={{ width:26, color:C.creamDim, fontSize:"0.66rem" }}>{pct}</span>
              </div>
            ))}
          </div>}
          {[["Yelp","4.9"],["Google","5.0"],["Facebook","4.9"]].map(([name,score])=>(
            <div key={name} style={{ textAlign:"center" }}>
              <div className="bebas" style={{ fontSize:m?"1.5rem":"1.9rem", color:C.cream }}>{score}</div>
              <div style={{ fontSize:m?"0.66rem":"0.82rem", color:C.amber }}>★★★★★</div>
              <div style={{ fontSize:"0.58rem", letterSpacing:"0.12em", textTransform:"uppercase", color:C.creamDim, marginTop:2 }}>{name}</div>
            </div>
          ))}
        </div>
      </div>

      <section style={{ background:C.bg, padding:sp }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div className="reveal" style={{ background:`linear-gradient(135deg,rgba(234,34,121,0.09),rgba(236,176,81,0.05))`, border:`1px solid ${C.bPink}`, borderRadius:6, padding:m?"20px":"36px", textAlign:"center", marginBottom:28 }}>
            <blockquote className="dance" style={{ fontSize:"clamp(1.25rem,4vw,2.2rem)", color:C.cream, lineHeight:1.4, marginBottom:10 }}>
              "The quesabirria is a religious experience. That cheese pull dipped in consommé — I dream about it."
            </blockquote>
            <cite style={{ fontSize:"0.76rem", fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", color:C.pink, fontStyle:"normal" }}>— Jessica R. · Facebook · ★★★★★</cite>
          </div>

          <div style={{ display:"flex", gap:4, overflowX:"auto", marginBottom:24, paddingBottom:3, WebkitOverflowScrolling:"touch" }}>
            {platforms.map(({id,label})=>(
              <button key={id} onClick={()=>setFilter(id)} className="bebas" style={{ flexShrink:0, fontSize:"0.8rem", letterSpacing:"0.11em", padding:"8px 16px", border:`2px solid`, borderColor:filter===id?C.pink:C.bPink, background:filter===id?C.pink:"transparent", color:filter===id?"#fff":C.creamDim, cursor:"pointer", borderRadius:3, minHeight:38 }}>{label}</button>
            ))}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:m?"1fr":"repeat(3,1fr)", gap:m?10:16 }}>
            {filtered.map((r,i)=>(
              <div key={i} style={{ background:C.bg2, border:`1px solid ${C.bPink}`, borderRadius:4, padding:m?"16px":"22px", display:"flex", flexDirection:"column", gap:9, position:"relative" }}>
                <div style={{ position:"absolute", top:6, right:12, fontFamily:"'Dancing Script',cursive", fontSize:"3rem", color:C.pink, opacity:0.1, lineHeight:1 }}>"</div>
                <div style={{ fontSize:"0.82rem", color:C.amber, letterSpacing:3 }}>{"★".repeat(r.stars)}</div>
                <p style={{ fontSize:m?"0.85rem":"0.92rem", fontStyle:"italic", color:C.creamDim, lineHeight:1.7, flex:1 }}>"{r.text}"</p>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:5 }}>
                  <div>
                    <div style={{ fontSize:"0.66rem", fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", color:C.pink }}>— {r.name}</div>
                    <div style={{ fontSize:"0.6rem", color:C.creamDim }}>{r.date}</div>
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:3 }}>
                    <span style={{ fontSize:"0.6rem", color:C.creamDim, background:"rgba(255,255,255,0.05)", padding:"2px 6px", borderRadius:2 }}>{r.platform}</span>
                    {r.dish&&<span style={{ fontSize:"0.58rem", fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:C.bg, background:C.amber, padding:"2px 6px", borderRadius:2 }}>{r.dish}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap", marginTop:32 }}>
            {[["Yelp","Read reviews","https://www.yelp.com/biz/outside-the-masa-indio-4"],["Facebook","Leave a review","https://www.facebook.com/outsidethemasa"],["Instagram","Follow us","https://www.instagram.com/outsidethemasa"]].map(([name,action,url])=>(
              <a key={name} href={url} target="_blank" rel="noreferrer" style={{ display:"flex", alignItems:"center", gap:9, background:C.bg2, border:`1px solid ${C.bPink}`, padding:m?"11px 18px":"13px 22px", borderRadius:4, textDecoration:"none", flex:m?"1 1 auto":undefined, justifyContent:m?"center":undefined }}>
                <div>
                  <div className="bebas" style={{ fontSize:"0.88rem", letterSpacing:"0.11em", color:C.amber }}>{name}</div>
                  <div style={{ fontSize:"0.6rem", color:C.creamDim, letterSpacing:"0.08em", textTransform:"uppercase" }}>{action} →</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <div style={{ background:C.bg3, borderTop:`1px solid ${C.bPink}`, padding:m?"36px 18px":"60px 44px", textAlign:"center" }}>
        <div className="dance" style={{ fontSize:"clamp(1.7rem,6vw,3rem)", color:C.cream, marginBottom:7 }}>Tried the Masa?</div>
        <p style={{ fontSize:"0.93rem", color:C.creamDim, marginBottom:18 }}>Share your experience and let the world know about the best birria in the Coachella Valley.</p>
        <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" }}>
          <BtnPink href="https://www.yelp.com/biz/outside-the-masa-indio-4" style={m?{flex:"1 1 auto"}:{}}>Leave a Yelp Review</BtnPink>
          <BtnOutline href="https://www.facebook.com/outsidethemasa" style={m?{flex:"1 1 auto"}:{}}>Review on Facebook</BtnOutline>
        </div>
      </div>
      <Footer navigate={navigate} />
    </div>
  );
}

/* ═══════════════════════════════════════════════  FIND US  ═══════════════════════════════════════════════ */
function FindUsPage({ navigate }) {
  useReveal();
  const m = useIsMobile();
  const sp = m?"44px 18px":"76px 44px";
  const { content } = useSite();
  const hours = (content.hours && content.hours.length > 0) ? content.hours : DEFAULT_CONTENT.hours;
  const days = hours.map(h => h.day);
  const today = new Date().getDay();

  return (
    <div className="page-fade" style={{ paddingTop:m?94:110 }}>
      <PageHero eyebrow="Restaurant · Food Truck · Online Ordering" title="FIND US" script="We're closer than you think." subtitle="Visit our restaurant in Indio, track our food truck, or order online for pickup." />

      <section style={{ background:C.bg, padding:sp }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <SectionLabel>Dine In</SectionLabel>
          <SectionTitle bebas="The Restaurant" />
          <Divider />
          <div style={{ display:"grid", gridTemplateColumns:m?"1fr":"1.1fr 1fr", gap:m?20:56, alignItems:"start" }}>
            <div className="reveal">
              <div style={{ aspectRatio:"4/3", borderRadius:6, overflow:"hidden", border:`2px solid ${C.bPink}` }}>
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3312!2d-116.218!3d33.7209!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z43905+Clinton+St+Indio+CA!5e0!3m2!1sen!2sus!4v1"
                  style={{ width:"100%", height:"100%", border:"none", filter:"grayscale(1) invert(0.88) hue-rotate(180deg)", opacity:0.9 }} allowFullScreen loading="lazy" title="Map" />
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:10 }}>
                <a href="https://maps.google.com/?q=43905+Clinton+St+Suite+5+Indio+CA" target="_blank" rel="noreferrer" style={{ background:C.bg2, border:`1px solid ${C.bPink}`, borderRadius:4, padding:"14px 10px", textAlign:"center", textDecoration:"none", display:"flex", flexDirection:"column", gap:5, alignItems:"center" }}>
                  <span style={{ fontSize:"1.5rem" }}>🗺️</span>
                  <span className="bebas" style={{ fontSize:"0.78rem", letterSpacing:"0.11em", color:C.amber }}>Get Directions</span>
                </a>
                <a href="https://outside-the-masa.square.site/" target="_blank" rel="noreferrer" style={{ background:C.bg2, border:`1px solid ${C.bPink}`, borderRadius:4, padding:"14px 10px", textAlign:"center", textDecoration:"none", display:"flex", flexDirection:"column", gap:5, alignItems:"center" }}>
                  <span style={{ fontSize:"1.5rem" }}>📱</span>
                  <span className="bebas" style={{ fontSize:"0.78rem", letterSpacing:"0.11em", color:C.amber }}>Order Pickup</span>
                </a>
              </div>
            </div>

            <div className="reveal" style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {[
                {icon:"📍",title:"Restaurant Address",body:"43-905 Clinton St, Suite 5\nIndio, California 92201"},
                {icon:"📱",title:"Order Online",link:"https://outside-the-masa.square.site/"},
                {icon:"🎪",title:"Catering & Events",cta:true},
                {icon:"🛍️",title:"Merchandise",merch:true},
              ].map(({icon,title,body,link,cta,merch},i)=>(
                <div key={i} style={{ background:C.bg2, border:`1px solid ${C.bPink}`, borderRadius:4, padding:"14px 16px", display:"flex", gap:12, alignItems:"flex-start" }}>
                  <div style={{ width:38, height:38, borderRadius:"50%", border:`1px solid ${C.bAmb}`, background:"rgba(236,176,81,0.07)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1rem", flexShrink:0 }}>{icon}</div>
                  <div>
                    <h4 className="bebas" style={{ fontSize:"0.75rem", letterSpacing:"0.16em", color:C.amber, marginBottom:3 }}>{title}</h4>
                    {body&&<p style={{ fontSize:"0.88rem", color:C.creamDim, lineHeight:1.6, whiteSpace:"pre-line" }}>{body}</p>}
                    {link&&<a href={link} target="_blank" rel="noreferrer" style={{ fontSize:"0.86rem", color:C.pinkLight }}>{link.replace("http://","")}</a>}
                    {cta&&<button onClick={()=>navigate("catering")} style={{ background:"none", border:"none", cursor:"pointer", fontSize:"0.86rem", color:C.pinkLight, padding:0, textDecoration:"underline" }}>Book us for your event →</button>}
                    {merch&&<a href="https://www.outsidethemasa.com/category/all-products" target="_blank" rel="noreferrer" style={{ fontSize:"0.86rem", color:C.pinkLight }}>Shop our merch store →</a>}
                  </div>
                </div>
              ))}
              <div style={{ background:C.bg2, border:`1px solid ${C.bPink}`, borderRadius:4, padding:"14px 16px" }}>
                <div style={{ display:"flex", gap:12 }}>
                  <div style={{ width:38, height:38, borderRadius:"50%", border:`1px solid ${C.bAmb}`, background:"rgba(236,176,81,0.07)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1rem", flexShrink:0 }}>🕐</div>
                  <div style={{ flex:1 }}>
                    <h4 className="bebas" style={{ fontSize:"0.75rem", letterSpacing:"0.16em", color:C.amber, marginBottom:7 }}>Hours of Operation</h4>
                    {days.map((day,i)=>(
                      <div key={i} style={{ display:"flex", justifyContent:"space-between", fontSize:i===today?"0.84rem":"0.8rem", padding:"3px 0", borderBottom:"1px solid rgba(255,255,255,0.04)", color:i===today?C.pink:C.creamDim, fontWeight:i===today?700:400 }}>
                        <span>{day}{i===today&&<span style={{ marginLeft:5, fontSize:"0.56rem", background:C.pink, color:"#fff", padding:"1px 5px", borderRadius:2, letterSpacing:"0.08em", textTransform:"uppercase" }}>Today</span>}</span>
                        <span style={{ color:i===today?C.pink:C.cream, fontWeight:700 }}>{hours[i].open} – {hours[i].close}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOD TRUCK */}
      <section style={{ background:C.bg2, padding:sp }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div className="reveal" style={{ display:"flex", flexDirection:m?"column":"row", alignItems:m?"flex-start":"center", justifyContent:"space-between", marginBottom:24, gap:12 }}>
            <div><SectionLabel>On the Road</SectionLabel><SectionTitle bebas="Food Truck Schedule" /></div>
            <div style={{ display:"flex", alignItems:"center", gap:7, background:"rgba(234,34,121,0.1)", border:`1px solid ${C.bPink}`, padding:"7px 13px", borderRadius:20, fontSize:"0.7rem", fontWeight:700, letterSpacing:"0.11em", textTransform:"uppercase", color:C.pink }}>
              <span style={{ width:7, height:7, background:C.pink, borderRadius:"50%", animation:"blink 1.5s infinite", flexShrink:0 }} />Follow for Live Updates
            </div>
          </div>
          <div className="reveal" style={{ display:"grid", gridTemplateColumns:m?"1fr":"repeat(3,1fr)", gap:m?10:16 }}>
            {[
              {day:"Mon – Wed",area:"Indio Area",time:"Hours vary",desc:"Rotating locations around Indio. Check Instagram or Facebook for today's exact spot.",active:true},
              {day:"Thu – Fri",area:"Coachella Valley",time:"Lunch & Dinner",desc:"We frequently appear at local businesses, parking lots, and community events."},
              {day:"Weekends",area:"Events & Festivals",time:"Event hours",desc:"Weekends are event season. We participate in local festivals and markets across the Valley."},
            ].map(({day,area,time,desc,active},i)=>(
              <div key={i} style={{ background:active?"rgba(234,34,121,0.05)":C.bg, border:`1px solid`, borderColor:active?C.pink:C.bPink, borderRadius:4, padding:m?"16px":"20px" }}>
                <div className="bebas" style={{ fontSize:"0.79rem", letterSpacing:"0.17em", color:C.amber, marginBottom:3 }}>📍 {day}</div>
                <div className="dance" style={{ fontSize:"1.25rem", color:C.cream, marginBottom:3 }}>{area}</div>
                <div style={{ fontSize:"0.78rem", color:C.creamDim, marginBottom:6 }}>⏰ {time}</div>
                <p style={{ fontSize:"0.84rem", color:C.creamDim, lineHeight:1.6 }}>{desc}</p>
              </div>
            ))}
          </div>
          <div className="reveal" style={{ marginTop:16, textAlign:"center", padding:m?"18px":"20px", background:"rgba(236,176,81,0.05)", border:`1px solid ${C.bAmb}`, borderRadius:4 }}>
            <p style={{ fontSize:"0.88rem", color:C.creamDim, marginBottom:12 }}>🚚 Follow us on social media for real-time food truck locations.</p>
            <div style={{ display:"flex", justifyContent:"center", gap:8, flexWrap:"wrap" }}>
              {[["📷 Instagram","https://www.instagram.com/outsidethemasa"],["👍 Facebook","https://www.facebook.com/outsidethemasa"],["🎵 TikTok","https://www.tiktok.com/@outsidethemasa760"],["⭐ Yelp","https://www.yelp.com/biz/outside-the-masa-indio-4"]].map(([label,url])=>(
                <a key={label} href={url} target="_blank" rel="noreferrer" style={{ fontSize:"0.68rem", fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:C.cream, border:`1px solid ${C.bPink}`, padding:"8px 14px", borderRadius:3, textDecoration:"none", minHeight:38, display:"flex", alignItems:"center" }}>{label}</a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section style={{ background:C.bg, padding:sp }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div className="reveal" style={{ background:`linear-gradient(135deg,rgba(234,34,121,0.09),rgba(236,176,81,0.05))`, border:`1px solid ${C.bPink}`, borderRadius:6, padding:m?"28px 18px":"44px", textAlign:"center" }}>
            <div className="dance" style={{ fontSize:"clamp(1.7rem,6vw,3rem)", color:C.cream, marginBottom:7 }}>Rep the Masa 🌶️</div>
            <p style={{ fontSize:"0.93rem", color:C.creamDim, marginBottom:18 }}>Show your love for Outside the Masa with our official merchandise.</p>
            <BtnPink href="https://www.outsidethemasa.com/category/all-products" style={m?{width:"100%",justifyContent:"center"}:{}}>Shop Merch</BtnPink>
          </div>
        </div>
      </section>
      <Footer navigate={navigate} />
    </div>
  );
}

/* ═══════════════════════════════════════════════
   ADMIN SYSTEM
═══════════════════════════════════════════════ */

/* ── Default content (used if no localStorage data exists) ── */
const DEFAULT_CONTENT = {
  announcement: "🔥 NOW WITH A RESTAURANT — 43-905 Clinton St, Indio CA",
  announcementLink: "ORDER →",
  announcementUrl: "https://outside-the-masa.square.site/",
  announcementVisible: true,
  heroEyebrow: "Authentic Birria · Jalisco, México",
  heroSubtitle: "A family recipe from Jalisco. Slow-cooked, rich, and bursting with bold flavors. Now at our restaurant in Indio, CA.",
  heroAddress: "📍 43-905 Clinton St, Suite 5 · Indio, CA",
  storyP1: "Picture tender, succulent birria — a traditional dish from Jalisco that holds a special place in our hearts and culinary heritage.",
  storyP2: "We started as a food truck, fueled by passion and familia. Today, we have a full restaurant — same soul, bigger table.",
  restaurantAddress: "43-905 Clinton St, Suite 5\nIndio, California 92201",
  orderUrl: "https://outside-the-masa.square.site/",
  squareStoreUrl: "https://outside-the-masa.square.site/",
  instagramUrl: "https://www.instagram.com/outsidethemasa",
  facebookUrl: "https://www.facebook.com/outsidethemasa",
  tiktokUrl: "https://www.tiktok.com/@outsidethemasa760",
  yelpUrl: "https://www.yelp.com/biz/outside-the-masa-indio-4",
  galleryImages: [
    "https://static.wixstatic.com/media/8e34ba_8afdc3ef21be477d9e1a4c415ead5611~mv2.jpg",
    "https://static.wixstatic.com/media/8e34ba_21c76737ddd94d019ad0633e0722ff36~mv2.jpg",
    "https://static.wixstatic.com/media/8e34ba_97fd39ced86341459aad3336080d649b~mv2.jpg",
    "https://static.wixstatic.com/media/8e34ba_0698ee5e7ced4ca2a9c2a079e2b9e746~mv2.jpg",
    "https://static.wixstatic.com/media/8e34ba_ef35c304ca8c483494a9c87be59969a7~mv2.jpg",
  ],
  hours: [
    {day:"Sunday",    open:"9:00 AM",  close:"2:00 PM"},
    {day:"Monday",    open:"CLOSED",   close:""},
    {day:"Tuesday",   open:"11:00 AM", close:"8:00 PM"},
    {day:"Wednesday", open:"11:00 AM", close:"3:00 PM"},
    {day:"Thursday",  open:"11:00 AM", close:"8:00 PM"},
    {day:"Friday",    open:"11:00 AM", close:"8:00 PM"},
    {day:"Saturday",  open:"9:00AM - 2:00PM", close:"4:00PM - 8:00PM"},
  ],
};

const DEFAULT_MENU_ITEMS = [
  /* ── BIRRIA SPECIALTIES ── */
  {id:1, cat:"birria", name:"Los Chilaquillers", price:"$18", desc:"The ones everyone's talking about. Red or green salsa, topped with birria, beans, queso fresco, Mexican crema, onions, and cilantro. House-made tortilla chips.", badge:"Fan Favorite", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775404428/chilaquiles_mixtos_con_birria.jpg"},
  {id:2, cat:"birria", name:"Los Tres con Queso", price:"$16", desc:"Crispy birria tacos with melted cheese. Crunchy and dangerously good. Complimentary consommé available upon request.", badge:"Best Seller", badgeAmber:true, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775352969/birriaconqueso.jpg"},
  {id:3, cat:"birria", name:"Los Tres de Siempre", price:"$14", desc:"Classic birria tacos on double corn tortillas, topped with onion and cilantro.", badge:"Classic", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775404464/tacos_de_birria.jpg"},
  {id:4, cat:"birria", name:"La Quesadilla", price:"$16", desc:"Large flour tortilla filled with melted Monterey cheese and birria, grilled until crispy, finished with onion and cilantro… perfect for sharing… or not.", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775404450/quesa_birria.jpg"},
  {id:5, cat:"birria", name:"Tacos Planchados", price:"$15", desc:"Three soft tacos with a crispy cheese crust grilled on the flat top, filled with birria and finished with onion and cilantro.", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775352970/Quesabirria.jpg"},
  {id:6, cat:"birria", name:"Birria en su Jugo", price:"$15", desc:"The classic Jalisco-style recipe served in rich consommé. Tender birria accompanied with onion, cilantro, and warm tortillas. Available while supplies last.", badge:"Traditional", badgeAmber:true, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775404417/birria_en_su_jugo.jpg"},
  /* ── SIDES & EXTRAS ── */
  {id:7, cat:"sides", name:"Birria Fries", price:"$15", desc:"French fries loaded with nacho cheese, beef birria, crema, onion, and cilantro.", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775404421/birria_fries.jpg"},
  {id:8, cat:"sides", name:"Birria Nachos", price:"$15", desc:"Crispy house-made tortilla chips loaded with nacho cheese, birria, crema, onion, and cilantro.", badge:"Fan Favorite", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775352969/birrianachos.jpg"},
  {id:9, cat:"sides", name:"Chips & Salsa", price:"$7", desc:"Crunchy tortilla chips served with our house salsas. A perfect snack to share.", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775400124/salsas.png"},
  {id:10, cat:"sides", name:"Trio Botanero", price:"$8", desc:"The perfect snack plate. Tortilla chips served with nacho cheese, refried beans topped with queso fresco, and our house salsa.", badge:"", badgeAmber:true, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1774465006/Guacamole_con_Totopos.png"},
  {id:11, cat:"sides", name:"Cheese Fries", price:"$8", desc:"French fries loaded with classic yellow nacho cheese.", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1782443158/cheese_fries.png"},
  {id:12, cat:"sides", name:"Kids Quesadilla", price:"$8", desc:"Flour tortilla with melted Monterey cheese, served with fries and refried beans. Add birria +$3.", badge:"Kids", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775404440/kids_quesadilla.jpg"},
  /* ── DRINKS ── */
  {id:13, cat:"drinks", name:"Agua Fresca del Día", price:"$5", desc:"Freshly made agua fresca of the day (20oz). Rotating flavors — Horchata, Jamaica, Tamarindo, and more.", badge:"Refreshing", badgeAmber:true, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775404416/agua_fresca.jpg"},
  {id:14, cat:"drinks", name:"Refrescos", price:"$3–$4.50", desc:"Lata $3 · Botella (355ml) $4.50. Assorted Mexican sodas — Jarritos, Topo Chico, and more.", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775404455/sodas.jpg"},
  {id:15, cat:"drinks", name:"Botella de Agua", price:"$2", desc:"Refreshing bottled water.", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1782439991/water_bottle.png"},
  {id:16, cat:"drinks", name:"Café de La Casa", price:"$3", desc:"Our house coffee — hot and fresh.", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1782440007/coffee.png"},
  {id:17, cat:"drinks", name:"Cerveza Importada", price:"$6", desc:"12oz imported beer. Caguama 32oz $14. Cubetazo de 6 cervezas $35.", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1782441801/mexican_beer.png"},
  {id:18, cat:"drinks", name:"Cerveza Doméstica", price:"$5", desc:"12oz domestic beer. Cubetazo de 6 cervezas $28.", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1782441801/USA_Beers.png"},
  {id:19, cat:"drinks", name:"Michelada", price:"$8", desc:"Chamoy hecho en casa 100% natural.", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1782441800/Michelada.png"},
  /* ── EXTRAS ── */
  {id:20, cat:"extras", name:"Grilled Jalapeños", price:"$1.00", desc:"", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1782441793/grilled_jalapenos.png"},
  {id:21, cat:"extras", name:"Side Beans", price:"$2.00", desc:"", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1782441793/Side_beans.png"},
  {id:22, cat:"extras", name:"Grilled Onions", price:"$0.75", desc:"", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1782441792/grilled_onion.png"},
  {id:23, cat:"extras", name:"Extra Queso", price:"$0.75", desc:"", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1782441784/extra_queso.png"},
  {id:24, cat:"extras", name:"Pickled Red Onions", price:"$0.75", desc:"", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1782441784/pickle_red_onions.png"},
  {id:25, cat:"extras", name:"Extra Egg", price:"$0.75", desc:"", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1782441783/extra_egg.png"},
  {id:26, cat:"extras", name:"Extra Tortillas", price:"$1.00", desc:"", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1782441783/Extra_tortillas.png"},
  {id:27, cat:"extras", name:"Extra Consomé", price:"$1.00", desc:"", badge:"", badgeAmber:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1782442907/extra_consome.png"},
];


/* ── Context so every page can read live content ── */
const SiteCtx = createContext(null);
const useSite = () => useContext(SiteCtx);

/* ── API-based persistence (saves for ALL visitors) ── */
const API = "/api/content";
const ADMIN_PASSWORD = "birria2026"; // must match env var in Vercel

/* Load from API — falls back to defaults if API not ready */
async function fetchSiteData() {
  try {
    const res = await fetch(API);
    if (!res.ok) throw new Error("API error");
    return await res.json();
  } catch {
    return { site: null, menu: null, order: null };
  }
}

/* Save all content through the API */
async function saveSiteData({ site, menu, order }) {
  const res = await fetch(API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password: ADMIN_PASSWORD, site, menu, order }),
  });
  if (!res.ok) throw new Error("Save failed");
  /* wipe any old localStorage cache so it never conflicts with Supabase */
  try {
    localStorage.removeItem("otm_site_v2");
    localStorage.removeItem("otm_menu_v2");
    localStorage.removeItem("otm_order_v2");
    localStorage.removeItem("otm_site");
    localStorage.removeItem("otm_menu");
  } catch {}
  return res.json();
}

/* ── localStorage fallbacks (legacy — kept for migration only) ── */
const STORAGE_KEY = "otm_site_v2";
const MENU_KEY    = "otm_menu_v2";
const ORDER_KEY   = "otm_order_v2";

function loadContent() {
  return { ...DEFAULT_CONTENT };
}
function loadMenu() {
  return JSON.parse(JSON.stringify(DEFAULT_MENU_ITEMS));
}
function loadOrderItems() {
  return null;
}

/* ══════════════════════════════════════════════════════════════
   ADMIN SYSTEM  — password: birria2026
══════════════════════════════════════════════════════════════ */

/* ─── Reusable admin sub-components ─── */
function ALabel({ c }) { return <span style={{ display:"block", fontSize:"0.58rem", fontWeight:700, letterSpacing:"0.22em", textTransform:"uppercase", color:C.pink, marginBottom:5 }}>{c}</span>; }

function AInput({ val, onChange, type="text", ph="", rows, disabled }) {
  const s = { background:"rgba(255,255,255,0.05)", border:`1px solid ${C.bPink}`, color: disabled?"rgba(255,255,255,0.3)":C.cream, padding:"10px 12px", borderRadius:3, fontFamily:"'Nunito',sans-serif", fontSize:"0.9rem", outline:"none", width:"100%", resize:rows?"vertical":undefined };
  const fo = e=>{ if(!disabled) e.target.style.borderColor=C.pink; };
  const bl = e=>e.target.style.borderColor=C.bPink;
  if (rows) return <textarea value={val||""} onChange={e=>onChange(e.target.value)} placeholder={ph} rows={rows} style={s} onFocus={fo} onBlur={bl} disabled={disabled} />;
  return <input type={type} value={val||""} onChange={e=>onChange(e.target.value)} placeholder={ph} style={s} onFocus={fo} onBlur={bl} disabled={disabled} />;
}

function AField({ label, val, onChange, type, ph, rows, hint, disabled }) {
  return (
    <div style={{ marginBottom:13 }}>
      <ALabel c={label} />
      <AInput val={val} onChange={onChange} type={type} ph={ph} rows={rows} disabled={disabled} />
      {hint && <p style={{ fontSize:"0.68rem", color:"rgba(255,255,255,0.3)", marginTop:3 }}>{hint}</p>}
    </div>
  );
}

function ACard({ title, accent, children }) {
  return (
    <div style={{ background:C.bg2, border:`1px solid ${accent||C.bPink}`, borderRadius:6, padding:"18px 16px", marginBottom:16 }}>
      {title && <div className="bebas" style={{ fontSize:"0.8rem", letterSpacing:"0.15em", color:C.amber, marginBottom:14, paddingBottom:8, borderBottom:"1px solid rgba(255,255,255,0.06)" }}>{title}</div>}
      {children}
    </div>
  );
}

function ABtn({ children, onClick, color, outline, small, full }) {
  const bg = outline ? "transparent" : (color||C.pink);
  const s = { fontFamily:"'Bebas Neue',sans-serif", fontSize:small?"0.76rem":"0.88rem", letterSpacing:"0.14em", color:outline?(color||C.amber):"#fff", background:bg, border:`2px solid ${outline?(color||C.amber):(color||C.pink)}`, padding:small?"6px 12px":"10px 20px", borderRadius:3, cursor:"pointer", display:"inline-flex", alignItems:"center", justifyContent:"center", gap:6, minHeight:34, width:full?"100%":undefined };
  return <button onClick={onClick} style={s}>{children}</button>;
}

/* ─── Image uploader: drag-and-drop + URL paste + file picker ─── */
function ImageUploader({ images, onChange, maxImages=8, label="" }) {
  const [urlVal, setUrlVal] = useState("");
  const [drag, setDrag]     = useState(false);
  const inputId = `img-${label.replace(/\W/g,"")}`;

  const toB64 = f => new Promise((res,rej)=>{ const r=new FileReader(); r.onload=()=>res(r.result); r.onerror=rej; r.readAsDataURL(f); });

  const addFiles = async files => {
    const slots = maxImages - images.length;
    const picked = Array.from(files).slice(0, slots);
    const b64s = await Promise.all(picked.map(toB64));
    onChange([...images, ...b64s]);
  };

  const addUrl = () => {
    const u = urlVal.trim();
    if (!u || images.length >= maxImages) return;
    if (!u.startsWith("http")) { alert("Paste a full URL starting with https://"); return; }
    onChange([...images, u]);
    setUrlVal("");
  };

  return (
    <div>
      <ALabel c={`${label} (${images.length}/${maxImages})`} />
      {images.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))", gap:8, marginBottom:10 }}>
          {images.map((src,i) => (
            <div key={i} style={{ position:"relative", borderRadius:4, overflow:"hidden", aspectRatio:"4/3", background:C.bg, border:`1px solid ${C.bPink}` }}>
              <img src={src} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} onError={e=>e.target.style.opacity=0.2} />
              <button onClick={()=>onChange(images.filter((_,idx)=>idx!==i))} style={{ position:"absolute", top:4, right:4, background:"rgba(234,34,121,0.9)", border:"none", color:"#fff", width:20, height:20, borderRadius:"50%", cursor:"pointer", fontSize:"0.65rem", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
            </div>
          ))}
        </div>
      )}
      {images.length < maxImages && (
        <>
          <div
            onDragOver={e=>{e.preventDefault();setDrag(true);}}
            onDragLeave={()=>setDrag(false)}
            onDrop={e=>{e.preventDefault();setDrag(false);if(e.dataTransfer.files.length)addFiles(e.dataTransfer.files);}}
            onClick={()=>document.getElementById(inputId).click()}
            style={{ border:`2px dashed ${drag?C.pink:C.bPink}`, borderRadius:4, padding:"16px", textAlign:"center", cursor:"pointer", marginBottom:8, background:drag?"rgba(234,34,121,0.05)":"transparent", transition:"all 0.2s" }}
          >
            <div style={{ fontSize:"1.4rem", marginBottom:4 }}>📁</div>
            <p style={{ fontSize:"0.78rem", color:C.creamDim }}>Click or drag &amp; drop images</p>
            <p style={{ fontSize:"0.66rem", color:"rgba(255,255,255,0.25)", marginTop:2 }}>JPG · PNG · WEBP</p>
            <input id={inputId} type="file" accept="image/*" multiple style={{ display:"none" }} onChange={e=>{if(e.target.files.length)addFiles(e.target.files);e.target.value="";}} />
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <AInput val={urlVal} onChange={setUrlVal} ph="Or paste image URL (https://...)" />
            <ABtn onClick={addUrl} small>Add</ABtn>
          </div>
        </>
      )}
      {images.length >= maxImages && <p style={{ fontSize:"0.73rem", color:C.amber, marginTop:4 }}>Max {maxImages} images — remove one to add more.</p>}
    </div>
  );
}

/* ─── Main AdminPanel component ─── */
function AdminPanel({ onClose, navigate, content, setContent, menuItems, setMenuItems, orderItems, setOrderItems }) {
  const m = useIsMobile();

  /* auth */
  const [authed, setAuthed] = useState(false);
  const [pwd,    setPwd]    = useState("");
  const [pwdErr, setPwdErr] = useState(false);

  /* tab */
  const [tab, setTab] = useState("topbar");

  /* drafts — init from live props */
  const [draft,      setDraft]      = useState(() => ({...content}));
  const [menuDraft,  setMenuDraft]  = useState(() => menuItems.map(i=>({...i})));
  const [orderDraft, setOrderDraft] = useState(() => (orderItems||[]).map(i=>({...i})));

  /* new item stubs */
  const [newMenu,  setNewMenu]  = useState({ cat:"birria", name:"", desc:"", price:"", badge:"", img:"" });

  /* toast */
  const [toast, setToast] = useState({ msg:"", err:false });
  const showToast = (msg, err=false) => { setToast({msg,err}); setTimeout(()=>setToast({msg:"",err:false}),3000); };

  /* login */
  const doLogin = () => {
    if (pwd === ADMIN_PASSWORD) { setAuthed(true); setPwdErr(false); }
    else { setPwdErr(true); setPwd(""); setTimeout(()=>setPwdErr(false),2500); }
  };

  /* save all to API — visible to ALL visitors */
  const saveAll = async () => {
    setToast({ msg:"💾 Saving...", err:false });
    try {
      await saveSiteData({
        site:  draft,
        menu:  menuDraft.map(i=>({...i})),
        order: orderDraft.map(i=>({...i})),
      });
      setContent({...draft});
      setMenuItems(menuDraft.map(i=>({...i})));
      setOrderItems(orderDraft.map(i=>({...i})));
      showToast("✅ Saved! Changes are now live.");
      setTimeout(() => {
        onClose();
        navigate("home");
      }, 1500);
    } catch(err) {
      showToast("❌ Save failed — check your connection and try again.", true);
    }
  };

  /* reset to defaults via API */
  const doReset = async () => {
    if (!window.confirm("Reset ALL content to defaults? Cannot be undone.")) return;
    const dc={...DEFAULT_CONTENT}, dm=JSON.parse(JSON.stringify(DEFAULT_MENU_ITEMS));
    setDraft(dc); setMenuDraft(dm);
    try {
      await saveSiteData({ site: dc, menu: dm, order: ORDER_ITEMS.map(i=>({...i})) });
      setContent(dc); setMenuItems(dm);
      showToast("♻️ Reset to defaults");
    } catch { showToast("❌ Reset failed — check connection", true); }
  };

  /* hours array helper */
  const getHours = () => draft.hours || DEFAULT_CONTENT.hours;
  const setHour = (i, field, val) => {
    const h=[...getHours()]; h[i]={...h[i],[field]:val};
    setDraft({...draft,hours:h});
  };

  /* layout */
  const ovStyle = { position:"fixed", inset:0, zIndex:9999, background:"rgba(8,6,11,0.96)", backdropFilter:"blur(8px)", overflowY:"auto" };
  const panStyle = { maxWidth:860, width:"100%", margin:"0 auto", padding:m?"12px 12px 100px":"26px 26px 100px" };
  const g2 = { display:"grid", gridTemplateColumns:m?"1fr":"1fr 1fr", gap:10 };
  const selStyle = { background:C.bg, border:`1px solid ${C.bPink}`, color:C.cream, padding:"10px 10px", borderRadius:3, fontFamily:"'Nunito',sans-serif", fontSize:"0.9rem", outline:"none", width:"100%" };

  /* ══ LOGIN SCREEN ══ */
  if (!authed) return (
    <div style={ovStyle} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ maxWidth:340, margin:"auto", padding:"24px 16px" }}>
        <div style={{ background:C.bg2, border:`2px solid ${C.bPink}`, borderRadius:10, padding:"36px 24px", textAlign:"center" }}>
          <img src={IMG.logo} alt="" style={{ width:68, height:68, borderRadius:"50%", objectFit:"contain", border:`2px solid ${C.bAmb}`, marginBottom:16 }} onError={e=>e.target.style.display="none"} />
          <div className="bebas" style={{ fontSize:"1.4rem", letterSpacing:"0.14em", color:C.amber, marginBottom:2 }}>Admin Dashboard</div>
          <div className="dance" style={{ fontSize:"1rem", color:C.pink, marginBottom:18 }}>Outside the Masa</div>
          <p style={{ fontSize:"0.8rem", color:C.creamDim, marginBottom:20 }}>Enter your password to edit the website</p>
          <input
            type="password" value={pwd}
            onChange={e=>setPwd(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&doLogin()}
            placeholder="Password" autoFocus
            style={{ background:"rgba(255,255,255,0.06)", border:`1px solid ${pwdErr?"#e55":C.bPink}`, color:C.cream, padding:"13px 14px", borderRadius:4, fontFamily:"'Nunito',sans-serif", fontSize:"1rem", outline:"none", width:"100%", textAlign:"center", letterSpacing:"0.2em", marginBottom:pwdErr?6:12 }}
          />
          {pwdErr && <p style={{ color:"#e55", fontSize:"0.8rem", marginBottom:8 }}>❌ Incorrect password</p>}
          <button onClick={doLogin}
            style={{ width:"100%", fontFamily:"'Bebas Neue',sans-serif", fontSize:"1rem", letterSpacing:"0.18em", color:"#fff", background:C.pink, border:"none", padding:"14px", borderRadius:4, cursor:"pointer", marginBottom:8 }}
            onMouseOver={e=>e.target.style.background=C.pinkLight} onMouseOut={e=>e.target.style.background=C.pink}>
            🔓 Log In
          </button>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:"0.75rem", color:"rgba(255,255,255,0.3)" }}>Cancel</button>
        </div>
      </div>
    </div>
  );

  /* ══ TABS CONFIG ══ */
  const TABS = [
    {id:"topbar",   icon:"📢", label:"Top Bar"},
    {id:"hero",     icon:"🏠", label:"Hero"},
    {id:"story",    icon:"📖", label:"Story"},
    {id:"gallery",  icon:"🖼",  label:"Gallery"},
    {id:"menu",     icon:"🍽",  label:"Menu"},
    {id:"order",    icon:"🛒", label:"Order"},
    {id:"catering", icon:"🚚", label:"Catering"},
    {id:"hours",    icon:"🕐", label:"Hours"},
    {id:"contact",  icon:"📍", label:"Contact"},
  ];

  /* ══ DASHBOARD ══ */
  return (
    <div style={ovStyle}>
      <div style={panStyle}>

        {/* ── Header bar ── */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, paddingBottom:16, borderBottom:`2px solid ${C.bPink}`, flexWrap:"wrap", gap:8 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <img src={IMG.logo} alt="" style={{ width:36, height:36, borderRadius:"50%", objectFit:"contain", border:`1px solid ${C.bAmb}` }} onError={e=>e.target.style.display="none"} />
            <div>
              <div className="bebas" style={{ fontSize:m?"1rem":"1.4rem", letterSpacing:"0.14em", color:C.amber }}>⚙ Outside the Masa — Admin</div>
              <p style={{ fontSize:"0.68rem", color:C.creamDim }}>Changes go live instantly when you save</p>
            </div>
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={doReset} style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"0.7rem", letterSpacing:"0.12em", background:"transparent", border:"1px solid rgba(255,80,80,0.35)", color:"rgba(255,90,90,0.75)", padding:"6px 11px", borderRadius:3, cursor:"pointer" }}>♻ Reset</button>
            <button onClick={onClose} style={{ background:"transparent", border:`1px solid ${C.bPink}`, color:C.pink, width:34, height:34, borderRadius:"50%", cursor:"pointer", fontSize:"1rem", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
          </div>
        </div>

        {/* ── Tab nav ── */}
        <div style={{ display:"flex", gap:3, flexWrap:"wrap", marginBottom:20, overflowX:"auto", paddingBottom:2, WebkitOverflowScrolling:"touch" }}>
          {TABS.map(({id,icon,label})=>(
            <button key={id} onClick={()=>setTab(id)}
              style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"0.74rem", letterSpacing:"0.1em", padding:"8px 12px", border:`2px solid ${tab===id?C.pink:C.bPink}`, background:tab===id?C.pink:"transparent", color:tab===id?"#fff":C.creamDim, cursor:"pointer", borderRadius:3, minHeight:34, whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:4 }}>
              <span>{icon}</span>{label}
            </button>
          ))}
        </div>

        {/* ════ TAB: TOP BAR ════ */}
        {tab==="topbar" && (
          <ACard title="📢 ANNOUNCEMENT BAR">
            <AField label="Text" val={draft.announcement} onChange={v=>setDraft({...draft,announcement:v})} ph="🔥 NOW WITH A RESTAURANT..." />
            <div style={g2}>
              <AField label="Link text (e.g. ORDER →)" val={draft.announcementLink} onChange={v=>setDraft({...draft,announcementLink:v})} />
              <AField label="Link URL" val={draft.announcementUrl} onChange={v=>setDraft({...draft,announcementUrl:v})} />
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
              <input type="checkbox" id="annVis" checked={!!draft.announcementVisible} onChange={e=>setDraft({...draft,announcementVisible:e.target.checked})} style={{ width:17, height:17, accentColor:C.pink, cursor:"pointer" }} />
              <label htmlFor="annVis" style={{ fontSize:"0.86rem", color:C.creamDim, cursor:"pointer" }}>Show bar on site</label>
            </div>
            <div style={{ background:"linear-gradient(90deg,#ea2279,#c01860,#ea2279)", padding:"9px 14px", borderRadius:3, textAlign:"center", fontSize:"0.7rem", fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:"#fff" }}>
              PREVIEW: {draft.announcement} &nbsp;<span style={{ color:C.amber }}>{draft.announcementLink}</span>
            </div>
          </ACard>
        )}

        {/* ════ TAB: HERO ════ */}
        {tab==="hero" && (<>
          <ACard title="🏠 HERO TEXT">
            <AField label="Eyebrow (small line above title)" val={draft.heroEyebrow} onChange={v=>setDraft({...draft,heroEyebrow:v})} ph="Authentic Birria · Jalisco, México" />
            <AField label="Subtitle paragraph" val={draft.heroSubtitle} onChange={v=>setDraft({...draft,heroSubtitle:v})} rows={3} />
            <AField label="Address line" val={draft.heroAddress} onChange={v=>setDraft({...draft,heroAddress:v})} ph="📍 43-905 Clinton St..." />
          </ACard>
          <ACard title="🖼 HERO BACKGROUND IMAGES" accent={C.bAmb}>
            <ImageUploader images={draft.heroImages||[]} onChange={imgs=>setDraft({...draft,heroImages:imgs})} maxImages={4} label="Hero photos (max 4)" />
          </ACard>
        </>)}

        {/* ════ TAB: STORY ════ */}
        {tab==="story" && (<>
          <ACard title="📖 OUR STORY TEXT">
            <AField label="Story paragraph 1" val={draft.storyP1} onChange={v=>setDraft({...draft,storyP1:v})} rows={4} />
            <AField label="Story paragraph 2" val={draft.storyP2} onChange={v=>setDraft({...draft,storyP2:v})} rows={4} />
          </ACard>
          <ACard title="🖼 STORY PHOTOS" accent={C.bAmb}>
            <ImageUploader images={draft.storyImages||[]} onChange={imgs=>setDraft({...draft,storyImages:imgs})} maxImages={3} label="Story photos (max 3)" />
          </ACard>
        </>)}

        {/* ════ TAB: GALLERY ════ */}
        {tab==="gallery" && (
          <ACard title="🖼 HOMEPAGE PHOTO GALLERY">
            <p style={{ fontSize:"0.78rem", color:C.creamDim, marginBottom:12 }}>Photos shown in the homepage mosaic strip. Drag &amp; drop or paste URLs. Up to 8 images.</p>
            <ImageUploader images={draft.galleryImages||[]} onChange={imgs=>setDraft({...draft,galleryImages:imgs})} maxImages={8} label="Gallery photos" />
          </ACard>
        )}

        {/* ════ TAB: MENU ITEMS ════ */}
        {tab==="menu" && (
          <div>
            {menuDraft.map((item, idx) => (
              <ACard key={item.id||idx}>
                <div style={{ display:"flex", gap:10, flexWrap:m?"wrap":"nowrap", alignItems:"flex-start" }}>
                  {/* thumb + img url */}
                  <div style={{ flexShrink:0, width:76 }}>
                    <div style={{ width:76, height:76, borderRadius:4, overflow:"hidden", border:`1px solid ${C.bPink}`, marginBottom:5 }}>
                      <img src={item.img} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={e=>e.target.style.opacity=0.2} />
                    </div>
                    <input value={item.img||""} onChange={e=>{ const d=[...menuDraft]; d[idx]={...d[idx],img:e.target.value}; setMenuDraft(d); }} placeholder="img url" style={{ background:"rgba(255,255,255,0.04)", border:`1px solid ${C.bPink}`, color:C.creamDim, padding:"3px 5px", borderRadius:2, fontSize:"0.6rem", fontFamily:"'Nunito',sans-serif", outline:"none", width:76 }} />
                  </div>
                  {/* fields */}
                  <div style={{ flex:1, display:"grid", gridTemplateColumns:m?"1fr":"1fr 1fr", gap:8 }}>
                    <div><ALabel c="Name" /><AInput val={item.name} onChange={v=>{ const d=[...menuDraft]; d[idx]={...d[idx],name:v}; setMenuDraft(d); }} /></div>
                    <div><ALabel c="Category" />
                      <select value={item.cat} onChange={e=>{ const d=[...menuDraft]; d[idx]={...d[idx],cat:e.target.value}; setMenuDraft(d); }} style={selStyle}>
                        <option value="birria">Birria Specialties</option>
                        <option value="tacos">Tacos &amp; Quesadillas</option>
                        <option value="sides">Sides &amp; Drinks</option>
                      </select>
                    </div>
                    <div style={{ gridColumn:m?"1":"1/-1" }}><ALabel c="Description" /><AInput val={item.desc} onChange={v=>{ const d=[...menuDraft]; d[idx]={...d[idx],desc:v}; setMenuDraft(d); }} /></div>
                    <div><ALabel c="Price (e.g. $12.99)" /><AInput val={item.price||""} onChange={v=>{ const d=[...menuDraft]; d[idx]={...d[idx],price:v}; setMenuDraft(d); }} ph="$0.00" /></div>
                    <div><ALabel c="Badge (optional)" /><AInput val={item.badge||""} onChange={v=>{ const d=[...menuDraft]; d[idx]={...d[idx],badge:v}; setMenuDraft(d); }} ph="Fan Favorite..." /></div>
                  </div>
                  <button onClick={()=>{ if(window.confirm(`Delete "${item.name}"?`)) setMenuDraft(menuDraft.filter((_,i)=>i!==idx)); }} style={{ background:"transparent", border:"1px solid rgba(255,70,70,0.3)", color:"rgba(255,90,90,0.8)", width:28, height:28, borderRadius:"50%", cursor:"pointer", fontSize:"0.75rem", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                </div>
              </ACard>
            ))}
            <ACard title="+ ADD NEW MENU ITEM" accent={C.bAmb}>
              <div style={g2}>
                <div><ALabel c="Name *" /><AInput val={newMenu.name} onChange={v=>setNewMenu({...newMenu,name:v})} ph="e.g. Tamale Plate" /></div>
                <div><ALabel c="Category" />
                  <select value={newMenu.cat} onChange={e=>setNewMenu({...newMenu,cat:e.target.value})} style={selStyle}>
                    <option value="birria">Birria Specialties</option>
                    <option value="tacos">Tacos &amp; Quesadillas</option>
                    <option value="sides">Sides &amp; Drinks</option>
                  </select>
                </div>
                <div style={{ gridColumn:m?"1":"1/-1" }}><ALabel c="Description" /><AInput val={newMenu.desc} onChange={v=>setNewMenu({...newMenu,desc:v})} ph="Describe the dish..." /></div>
                <div><ALabel c="Price" /><AInput val={newMenu.price} onChange={v=>setNewMenu({...newMenu,price:v})} ph="$0.00" /></div>
                <div><ALabel c="Badge" /><AInput val={newMenu.badge} onChange={v=>setNewMenu({...newMenu,badge:v})} ph="New, Seasonal..." /></div>
                <div style={{ gridColumn:m?"1":"1/-1" }}><ALabel c="Image URL (optional)" /><AInput val={newMenu.img} onChange={v=>setNewMenu({...newMenu,img:v})} ph="https://..." /></div>
              </div>
              <div style={{ marginTop:10 }}>
                <ABtn full onClick={()=>{
                  if (!newMenu.name.trim()) { showToast("❌ Name is required", true); return; }
                  const id = Math.max(0,...menuDraft.map(i=>i.id||0))+1;
                  setMenuDraft([...menuDraft, {...newMenu, id, badgeAmber:false}]);
                  setNewMenu({ cat:"birria", name:"", desc:"", price:"", badge:"", img:"" });
                  showToast("✅ Item added — click Save & Publish");
                }}>+ Add Menu Item</ABtn>
              </div>
            </ACard>
          </div>
        )}

        {/* ════ TAB: ORDER PAGE ════ */}
        {tab==="order" && (
          <div>
            <ACard title="🛒 ORDER PAGE — ITEMS & PRICES">
              <p style={{ fontSize:"0.78rem", color:C.creamDim, marginBottom:12 }}>These are the items shown on the Order Online page. Set each price and link to the direct Square product URL.</p>
              {orderDraft.map((item, idx) => (
                <div key={item.id||idx} style={{ background:C.bg, border:`1px solid ${C.bPink}`, borderRadius:4, padding:"12px", marginBottom:8, display:"flex", gap:10, flexWrap:m?"wrap":"nowrap", alignItems:"flex-start" }}>
                  <div style={{ width:64, height:64, borderRadius:4, overflow:"hidden", border:`1px solid ${C.bPink}`, flexShrink:0 }}>
                    <img src={item.img} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={e=>e.target.style.opacity=0.2} />
                  </div>
                  <div style={{ flex:1, display:"grid", gridTemplateColumns:m?"1fr":"1fr 1fr", gap:8 }}>
                    <div><ALabel c="Name" /><AInput val={item.name} onChange={v=>{ const d=[...orderDraft]; d[idx]={...d[idx],name:v}; setOrderDraft(d); }} /></div>
                    <div><ALabel c="Price (shown to customers)" /><AInput val={item.price||""} onChange={v=>{ const d=[...orderDraft]; d[idx]={...d[idx],price:v}; setOrderDraft(d); }} ph="$12.99" /></div>
                    <div style={{ gridColumn:m?"1":"1/-1" }}><ALabel c="Description" /><AInput val={item.desc} onChange={v=>{ const d=[...orderDraft]; d[idx]={...d[idx],desc:v}; setOrderDraft(d); }} /></div>
                    <div style={{ gridColumn:m?"1":"1/-1" }}><ALabel c="Square URL (direct product checkout)" /><AInput val={item.url||""} onChange={v=>{ const d=[...orderDraft]; d[idx]={...d[idx],url:v}; setOrderDraft(d); }} ph="https://outside-the-masa.square.site/product/..." /></div>
                    <div style={{ gridColumn:m?"1":"1/-1" }}><ALabel c="Image URL" /><AInput val={item.img||""} onChange={v=>{ const d=[...orderDraft]; d[idx]={...d[idx],img:v}; setOrderDraft(d); }} ph="https://..." /></div>
                  </div>
                  <button onClick={()=>setOrderDraft(orderDraft.filter((_,i)=>i!==idx))} style={{ background:"transparent", border:"1px solid rgba(255,70,70,0.3)", color:"rgba(255,90,90,0.8)", width:26, height:26, borderRadius:"50%", cursor:"pointer", fontSize:"0.7rem", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                </div>
              ))}
              <div style={{ marginTop:10 }}>
                <ABtn full outline onClick={()=>{
                  setOrderDraft([...orderDraft,{ id:`ord-${Date.now()}`, cat:"birria", catLabel:"Birria Specialties 🌶️", name:"New Item", desc:"", price:"", badge:"", badgePink:false, img:"", url:"https://outside-the-masa.square.site/" }]);
                  showToast("Row added — fill in the details above");
                }}>+ Add Item to Order Page</ABtn>
              </div>
            </ACard>
          </div>
        )}

        {/* ════ TAB: CATERING ════ */}
        {tab==="catering" && (<>
          <ACard title="🚚 CATERING TEXT">
            <AField label="Intro paragraph" val={draft.cateringIntro||""} onChange={v=>setDraft({...draft,cateringIntro:v})} rows={3} ph="Our catering services offer a fully customizable menu..." />
            <div style={g2}>
              <AField label="Minimum order note" val={draft.cateringMin||"Minimum 50 servings for all packages"} onChange={v=>setDraft({...draft,cateringMin:v})} />
              <AField label="Service area" val={draft.cateringArea||"Coachella Valley & surrounding areas"} onChange={v=>setDraft({...draft,cateringArea:v})} />
              <AField label="Advance notice" val={draft.cateringNotice||"Book 2–4 weeks in advance"} onChange={v=>setDraft({...draft,cateringNotice:v})} />
            </div>
          </ACard>
          <ACard title="🖼 CATERING PHOTOS" accent={C.bAmb}>
            <ImageUploader images={draft.cateringImages||[]} onChange={imgs=>setDraft({...draft,cateringImages:imgs})} maxImages={6} label="Catering photos (max 6)" />
          </ACard>
        </>)}

        {/* ════ TAB: HOURS ════ */}
        {tab==="hours" && (
          <ACard title="🕐 HOURS OF OPERATION">
            <p style={{ fontSize:"0.78rem", color:C.creamDim, marginBottom:12 }}>Today's day is highlighted automatically on the Find Us page.</p>
            {getHours().map((h,i)=>(
              <div key={h.day} style={{ display:"grid", gridTemplateColumns:"90px 1fr 1fr", gap:8, alignItems:"center", marginBottom:8, padding:"7px 9px", background:i===new Date().getDay()?"rgba(234,34,121,0.06)":"transparent", borderRadius:3, border:i===new Date().getDay()?`1px solid ${C.bPink}`:"1px solid transparent" }}>
                <span style={{ fontSize:"0.8rem", color:i===new Date().getDay()?C.pink:C.creamDim, fontWeight:700 }}>
                  {h.day}{i===new Date().getDay()&&<span style={{ marginLeft:4, fontSize:"0.56rem", background:C.pink, color:"#fff", padding:"1px 4px", borderRadius:2, letterSpacing:"0.08em", textTransform:"uppercase" }}>Today</span>}
                </span>
                <AInput val={h.open} onChange={v=>setHour(i,"open",v)} ph="11:00 AM" />
                <AInput val={h.close} onChange={v=>setHour(i,"close",v)} ph="9:00 PM" />
              </div>
            ))}
            <p style={{ fontSize:"0.68rem", color:"rgba(255,255,255,0.25)", marginTop:6 }}>Type "Closed" for days the restaurant is closed.</p>
          </ACard>
        )}

        {/* ════ TAB: CONTACT ════ */}
        {tab==="contact" && (
          <ACard title="📍 CONTACT INFO & LINKS">
            <AField label="Restaurant Address" val={draft.restaurantAddress} onChange={v=>setDraft({...draft,restaurantAddress:v})} rows={2} />
            <div style={g2}>
              <AField label="Square Order URL" val={draft.orderUrl} onChange={v=>setDraft({...draft,orderUrl:v})} hint="Used by all Order Online buttons" />
              <AField label="Square Store URL" val={draft.squareStoreUrl||""} onChange={v=>setDraft({...draft,squareStoreUrl:v})} hint="Shown on the Order page" />
              <AField label="Instagram URL" val={draft.instagramUrl} onChange={v=>setDraft({...draft,instagramUrl:v})} />
              <AField label="Facebook URL" val={draft.facebookUrl} onChange={v=>setDraft({...draft,facebookUrl:v})} />
              <AField label="TikTok URL" val={draft.tiktokUrl} onChange={v=>setDraft({...draft,tiktokUrl:v})} />
              <AField label="Yelp URL" val={draft.yelpUrl} onChange={v=>setDraft({...draft,yelpUrl:v})} />
            </div>
          </ACard>
        )}

        {/* ════ STICKY SAVE BAR ════ */}
        <div style={{ position:"sticky", bottom:0, background:"rgba(19,19,23,0.97)", borderTop:`2px solid ${C.bPink}`, padding:"12px 0", marginTop:20, display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, flexWrap:"wrap", backdropFilter:"blur(8px)" }}>
          <p style={{ fontSize:"0.73rem", color:C.creamDim }}>Tab: <strong style={{ color:C.amber }}>{TABS.find(t=>t.id===tab)?.label}</strong> · All changes save together</p>
          <button onClick={saveAll}
            style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:"0.95rem", letterSpacing:"0.15em", color:"#fff", background:C.pink, border:"none", padding:"12px 30px", borderRadius:4, cursor:"pointer", whiteSpace:"nowrap", boxShadow:`0 4px 16px rgba(234,34,121,0.4)` }}
            onMouseOver={e=>e.target.style.background=C.pinkLight} onMouseOut={e=>e.target.style.background=C.pink}>
            💾 Save &amp; Publish All
          </button>
        </div>
      </div>

      {/* ── Toast notification ── */}
      {toast.msg && (
        <div style={{ position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)", zIndex:99999, background:toast.err?"rgba(160,30,30,0.97)":C.bg2, border:`1px solid ${toast.err?"#e55":C.pink}`, color:C.cream, padding:"12px 22px", borderRadius:6, fontSize:"0.88rem", fontWeight:700, boxShadow:"0 8px 30px rgba(0,0,0,0.6)", animation:"slideDown 0.25s ease", whiteSpace:"nowrap" }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/* ─── Floating admin button — bottom-right corner ─── */
function AdminTrigger({ onClick }) {
  const m = useIsMobile();
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={()=>setHov(true)}
      onMouseLeave={()=>setHov(false)}
      title="Edit Website"
      style={{
        position:"fixed", bottom:m?18:24, right:m?18:24, zIndex:198,
        height:46, padding: hov&&!m?"0 18px 0 12px":"0 14px",
        minWidth:46, borderRadius:23,
        background:hov?C.pink:"rgba(19,19,23,0.9)",
        border:`2px solid ${hov?C.pinkLight:C.bAmb}`,
        color:hov?"#fff":C.amber,
        cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6,
        backdropFilter:"blur(10px)",
        boxShadow:hov?`0 6px 24px rgba(234,34,121,0.5)`:"0 4px 18px rgba(0,0,0,0.5)",
        transition:"all 0.22s", whiteSpace:"nowrap",
        fontFamily:"'Bebas Neue',sans-serif", fontSize:"0.85rem", letterSpacing:"0.12em",
        overflow:"hidden",
      }}
    >
      <span style={{ fontSize:"1.05rem", flexShrink:0 }}>⚙</span>
      {hov && !m && <span>Edit Website</span>}
    </button>
  );
}


/* ═══════════════════════════════════════════════
   PAGE: ORDER ONLINE — Native menu with Square deep-links
   Square blocks iframes by policy. The real solution is to show
   the full menu on-site and link each item directly to its Square
   checkout page, keeping customers in context until they're ready to buy.
═══════════════════════════════════════════════ */

const SQUARE_STORE = "https://outside-the-masa.square.site/";

/* Per-item Square product URLs — update these in the admin
   Contact tab → Square Store URL, or hard-code individual
   product page URLs from your Square dashboard here.         */
const ORDER_ITEMS = [
  /* ── BIRRIA ── */
  { id:"birria", cat:"birria", catLabel:"Birria Specialties 🌶️", name:"Birria", desc:"Slow-cooked beef birria from Jalisco — braised in dried chiles and spices. Rich, tender, unforgettable.", badge:"Signature", badgePink:true, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775352969/Birria.jpg", url:SQUARE_STORE },
  { id:"birria-con-queso", cat:"birria", catLabel:"Birria Specialties 🌶️", name:"Birria con Queso", desc:"Classic birria topped with melted cheese, served with a full cup of house consommé for dipping.", badge:"Must Try", badgePink:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775352969/birriaconqueso.jpg", url:SQUARE_STORE },
  { id:"birria-nachos", cat:"birria", catLabel:"Birria Specialties 🌶️", name:"Birria Nachos", desc:"Crispy chips loaded with birria, melted cheese, jalapeños, crema, pico, and consommé on the side.", badge:"Fan Favorite", badgePink:true, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775352969/birrianachos.jpg", url:SQUARE_STORE },
  { id:"tamale-birria", cat:"birria", catLabel:"Birria Specialties 🌶️", name:"Tamale de Birria", desc:"Hand-wrapped masa tamale stuffed with juicy birria, sealed in corn husks. Ask about availability.", badge:"Chef's Pick", badgePink:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775352971/tamaldebirria.jpg", url:SQUARE_STORE },
  { id:"chilaquiles", cat:"birria", catLabel:"Birria Specialties 🌶️", name:"Chilaquiles", desc:"Tortilla chips simmered in salsa, topped with birria, crema, queso fresco, onion, and cilantro. Weekend special.", badge:"Weekend", badgePink:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775352969/chilaquiles.jpg", url:SQUARE_STORE },
  /* ── TACOS ── */
  { id:"quesabirria", cat:"tacos", catLabel:"Tacos &amp; More 🌮", name:"Quesabirria", desc:"Corn tortillas dipped in consommé, griddled with birria and melted cheese. Our best-seller.", badge:"Best Seller", badgePink:true, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775352970/Quesabirria.jpg", url:SQUARE_STORE },
  { id:"asada-tacos", cat:"tacos", catLabel:"Tacos &amp; More 🌮", name:"Tacos de Asada", desc:"Grilled carne asada on hand-pressed corn tortillas with onion, cilantro, and salsa roja.", badge:"", badgePink:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775352971/tacosdeasada.jpg", url:SQUARE_STORE },
  { id:"taquitos", cat:"tacos", catLabel:"Tacos &amp; More 🌮", name:"Taquitos Dorados", desc:"Crispy rolled taquitos filled with birria, topped with crema, queso fresco, and guacamole.", badge:"Crispy", badgePink:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1774464995/Taquitos_Dorados.png", url:SQUARE_STORE },
  /* ── SIDES ── */
  { id:"guac", cat:"sides", catLabel:"Sides &amp; Drinks 🥤", name:"Guacamole &amp; Totopos", desc:"Fresh-smashed avocado with lime, tomato, white onion, and cilantro. Served with crispy totopos. Made to order.", badge:"Fresh", badgePink:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1774465006/Guacamole_con_Totopos.png", url:SQUARE_STORE },
  { id:"salsas", cat:"sides", catLabel:"Sides &amp; Drinks 🥤", name:"Salsas", desc:"House-made salsas — roja, verde, habanero, and seasonal specials. Made fresh daily.", badge:"Complimentary", badgePink:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775400124/salsas.png", url:SQUARE_STORE },
  { id:"agua", cat:"sides", catLabel:"Sides &amp; Drinks 🥤", name:"Aguas Frescas", desc:"Freshly made aguas frescas — Horchata, Jamaica, Tamarindo, and rotating seasonal flavors.", badge:"Refreshing", badgePink:false, img:"https://res.cloudinary.com/dmktopcmj/image/upload/v1775400122/aguas_frescas.png", url:SQUARE_STORE },
];

function OrderPage({ navigate }) {
  useReveal();
  const m = useIsMobile();
  const { content, orderItems: liveOrderItems } = useSite();
  const [activeCat, setActiveCat] = useState("all");
  const storeUrl = content.squareStoreUrl || SQUARE_STORE;

  /* Use live editable items, fall back to defaults */
  const items = (liveOrderItems && liveOrderItems.length > 0) ? liveOrderItems : ORDER_ITEMS;

  const cats = [
    { id:"all",    label:"All Items" },
    { id:"birria", label:"Birria 🌶️" },
    { id:"tacos",  label:"Tacos 🌮" },
    { id:"sides",  label:"Sides 🍟" },
    { id:"drinks", label:"Drinks 🥤" },
  ];

  const visible = activeCat === "all"
    ? items
    : items.filter(i => i.cat === activeCat);

  /* Group by category for section headers */
  const groups = {};
  visible.forEach(item => {
    if (!groups[item.cat]) groups[item.cat] = { label: item.catLabel, items: [] };
    groups[item.cat].items.push(item);
  });

  return (
    <div className="page-fade" style={{ paddingTop: m ? 94 : 110, background:C.bg, minHeight:"100vh" }}>

      {/* ── PAGE HERO ── */}
      <div style={{ background:`linear-gradient(160deg,#131317,#1a0d08,#131317)`, borderBottom:`1px solid ${C.bPink}`, padding: m ? "30px 18px 26px" : "40px 44px 32px", textAlign:"center", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse 70% 80% at 50% 100%,rgba(234,34,121,0.13) 0%,transparent 70%)" }} />
        <div style={{ position:"relative", zIndex:1 }}>
          <div style={{ fontSize:"0.67rem", fontWeight:700, letterSpacing:"0.3em", textTransform:"uppercase", color:C.pink, marginBottom:10, display:"flex", alignItems:"center", justifyContent:"center", gap:9 }}>
            <span style={{ width:18, height:2, background:C.pink, display:"block" }} />
            Order for Pickup · Indio, CA
            <span style={{ width:18, height:2, background:C.pink, display:"block" }} />
          </div>
          <div className="bebas" style={{ fontSize: m ? "clamp(3rem,11vw,5.5rem)" : "clamp(3.5rem,7vw,6rem)", WebkitTextStroke:`2px ${C.amber}`, color:"transparent", letterSpacing:"0.04em", lineHeight:0.88 }}>ORDER ONLINE</div>
          <div className="dance" style={{ fontSize: m ? "1.4rem" : "1.9rem", color:C.pink, marginTop:6, marginBottom:14 }}>Fresh. Fast. Delicious.</div>
          <p style={{ fontSize:"0.9rem", color:C.creamDim, maxWidth:460, margin:"0 auto 22px" }}>
            Browse our menu below and tap any item to go directly to our secure Square checkout — Apple Pay, Google Pay &amp; cards accepted.
          </p>
          {/* Main CTA */}
          <a
            href={storeUrl}
            target="_blank"
            rel="noreferrer"
            style={{ display:"inline-flex", alignItems:"center", gap:10, fontFamily:"'Bebas Neue',sans-serif", fontSize:"1rem", letterSpacing:"0.18em", color:"#fff", background:C.pink, padding: m ? "13px 28px" : "14px 36px", borderRadius:3, textDecoration:"none" }}
          >
            🛒 Go to Full Square Store
          </a>
        </div>
      </div>

      {/* ── TRUST BAR ── */}
      <div style={{ background:C.bg2, borderBottom:`1px solid ${C.bPink}`, padding:"12px 18px" }}>
        <div style={{ maxWidth:900, margin:"0 auto", display:"flex", alignItems:"center", justifyContent:"center", gap: m ? 12 : 28, flexWrap:"wrap" }}>
          {[["🔒","Secure Square Checkout"],["📱","Apple & Google Pay"],["⚡","Pickup Ready"],["🌶️","Made Fresh to Order"]].map(([icon,label])=>(
            <div key={label} style={{ display:"flex", alignItems:"center", gap:5, fontSize: m ? "0.65rem" : "0.72rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:C.creamDim }}>
              <span>{icon}</span>{label}
            </div>
          ))}
        </div>
      </div>

      {/* ── CATEGORY TABS ── */}
      <div style={{ background:C.bg, borderBottom:`1px solid ${C.bPink}`, padding: m ? "14px 14px 0" : "16px 44px 0", position:"sticky", top: m ? 94 : 110, zIndex:10 }}>
        <div style={{ display:"flex", gap:3, overflowX:"auto", paddingBottom:0, WebkitOverflowScrolling:"touch" }}>
          {cats.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setActiveCat(id)}
              className="bebas"
              style={{
                flexShrink:0,
                fontSize:"0.82rem", letterSpacing:"0.12em",
                padding: m ? "10px 16px" : "11px 22px",
                border:"none",
                borderBottom:`3px solid ${activeCat===id ? C.pink : "transparent"}`,
                background:"transparent",
                color: activeCat===id ? C.pink : C.creamDim,
                cursor:"pointer",
                transition:"all 0.2s",
                minHeight:44,
                whiteSpace:"nowrap",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── MENU ITEMS ── */}
      <div style={{ maxWidth:1200, margin:"0 auto", padding: m ? "24px 14px" : "40px 44px" }}>
        {Object.entries(groups).map(([catKey, group]) => (
          <div key={catKey} style={{ marginBottom: m ? 32 : 48 }}>

            {/* Category header */}
            {activeCat === "all" && (
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom: m ? 14 : 20 }}>
                <span className="bebas" style={{ fontSize: m ? "1.1rem" : "1.3rem", letterSpacing:"0.12em", color:C.amber, whiteSpace:"nowrap" }}>{group.label}</span>
                <div style={{ flex:1, height:1, background:C.bAmb }} />
              </div>
            )}

            {/* Grid */}
            <div style={{ display:"grid", gridTemplateColumns: m ? "1fr 1fr" : "repeat(auto-fill,minmax(280px,1fr))", gap: m ? 10 : 16 }}>
              {group.items.map(item => (
                <a
                  key={item.id}
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration:"none", display:"flex", flexDirection:"column", background:C.bg2, borderRadius:4, overflow:"hidden", border:`1px solid ${C.bPink}`, transition:"all 0.25s", cursor:"pointer" }}
                  onMouseOver={e => { e.currentTarget.style.borderColor=C.pink; e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow=`0 10px 30px rgba(234,34,121,0.2)`; }}
                  onMouseOut={e  => { e.currentTarget.style.borderColor=C.bPink; e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="none"; }}
                >
                  {/* Image */}
                  <div style={{ position:"relative", overflow:"hidden", height: m ? 130 : 190 }}>
                    <img
                      src={item.img}
                      alt={item.name}
                      loading="lazy"
                      style={{ width:"100%", height:"100%", objectFit:"cover", display:"block", transition:"transform 0.5s" }}
                      onMouseOver={e => e.target.style.transform="scale(1.05)"}
                      onMouseOut={e  => e.target.style.transform="scale(1)"}
                    />
                    {/* "Order" pill overlay */}
                    <div style={{ position:"absolute", top:8, right:8, background:"rgba(19,19,23,0.85)", backdropFilter:"blur(4px)", border:`1px solid ${C.bPink}`, borderRadius:20, padding:"4px 10px", display:"flex", alignItems:"center", gap:5 }}>
                      <span style={{ fontSize:"0.58rem", fontWeight:700, letterSpacing:"0.15em", textTransform:"uppercase", color:C.cream }}>🛒 Order</span>
                    </div>
                    {/* Badge */}
                    {item.badge && (
                      <div style={{ position:"absolute", bottom:8, left:8, background: item.badgePink ? C.pink : C.amberDim, color: item.badgePink ? "#fff" : C.bg, fontFamily:"'Bebas Neue',sans-serif", fontSize:"0.6rem", letterSpacing:"0.16em", padding:"3px 9px", borderRadius:2 }}>
                        {item.badge}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ padding: m ? "12px" : "18px", flex:1, display:"flex", flexDirection:"column", gap:6 }}>
                    <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:8 }}>
                      <div className="dance" style={{ fontSize: m ? "1.1rem" : "1.35rem", color:C.cream, lineHeight:1.2 }}>{item.name}</div>
                      {item.price && <span className="bebas" style={{ fontSize: m?"1rem":"1.1rem", color:C.amber, letterSpacing:"0.05em", flexShrink:0 }}>{item.price}</span>}
                    </div>
                    <p style={{ fontSize: m ? "0.76rem" : "0.88rem", color:C.creamDim, lineHeight:1.6, flex:1 }}>{item.desc}</p>
                    {/* CTA row */}
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:4 }}>
                      <span style={{ fontSize:"0.68rem", color:C.pink, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase" }}>Tap to order →</span>
                      <div style={{ width:28, height:28, borderRadius:"50%", background:C.pink, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.75rem" }}>→</div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ── HOW IT WORKS ── */}
      <div style={{ background:C.bg2, borderTop:`1px solid ${C.bPink}`, borderBottom:`1px solid ${C.bPink}`, padding: m ? "32px 18px" : "40px 44px" }}>
        <div style={{ maxWidth:900, margin:"0 auto", textAlign:"center" }}>
          <div className="bebas" style={{ fontSize: m ? "1rem" : "1.1rem", letterSpacing:"0.2em", color:C.amber, marginBottom:20 }}>HOW ORDERING WORKS</div>
          <div style={{ display:"grid", gridTemplateColumns: m ? "1fr" : "repeat(3,1fr)", gap: m ? 16 : 28 }}>
            {[
              { step:"1", icon:"🌮", title:"Pick Your Items", text:"Tap any item above to go directly to our Square store and add it to your cart." },
              { step:"2", icon:"💳", title:"Checkout Securely", text:"Pay with Apple Pay, Google Pay, or any card — processed securely by Square." },
              { step:"3", icon:"📍", title:"Pick Up Fresh", text:"Come to 43-905 Clinton St, Suite 5, Indio, CA. Your order is made fresh to order." },
            ].map(({ step, icon, title, text }) => (
              <div key={step} style={{ display:"flex", flexDirection: m ? "row" : "column", gap: m ? 14 : 10, alignItems: m ? "flex-start" : "center", textAlign: m ? "left" : "center" }}>
                <div style={{ width:44, height:44, borderRadius:"50%", background:C.pink, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.2rem", flexShrink:0 }}>{icon}</div>
                <div>
                  <div className="bebas" style={{ fontSize:"0.88rem", letterSpacing:"0.15em", color:C.amber, marginBottom:4 }}>STEP {step} — {title}</div>
                  <p style={{ fontSize:"0.86rem", color:C.creamDim, lineHeight:1.65 }}>{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── PICKUP INFO ── */}
      <div style={{ background:C.bg, padding: m ? "32px 18px" : "40px 44px", textAlign:"center" }}>
        <div style={{ maxWidth:800, margin:"0 auto", display:"grid", gridTemplateColumns: m ? "1fr" : "repeat(3,1fr)", gap: m ? 12 : 24 }}>
          {[
            { icon:"📍", title:"Pickup Address", text:"43-905 Clinton St, Suite 5\nIndio, California 92201" },
            { icon:"⏰", title:"Hours", text:"Mon–Wed: 11AM–8PM\nThu–Fri: 11AM–9PM\nWeekends: 11AM–10PM" },
            { icon:"📱", title:"Follow Us", text:"@outsidethemasa\nInstagram · Facebook · TikTok" },
          ].map(({ icon, title, text }) => (
            <div key={title} style={{ background:C.bg2, border:`1px solid ${C.bPink}`, borderRadius:4, padding: m ? "16px" : "20px" }}>
              <div style={{ fontSize:"1.4rem", marginBottom:7 }}>{icon}</div>
              <h4 className="bebas" style={{ fontSize:"0.78rem", letterSpacing:"0.16em", color:C.amber, marginBottom:5 }}>{title}</h4>
              <p style={{ fontSize:"0.83rem", color:C.creamDim, lineHeight:1.7, whiteSpace:"pre-line" }}>{text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── FULL STORE LINK ── */}
      <div style={{ background:`linear-gradient(135deg,rgba(234,34,121,0.08),rgba(236,176,81,0.04))`, border:`1px solid ${C.bPink}`, margin: m ? "0 14px" : "0 44px", borderRadius:6, padding: m ? "28px 18px" : "36px 44px", textAlign:"center" }}>
        <div className="dance" style={{ fontSize: m ? "1.6rem" : "2rem", color:C.cream, marginBottom:6 }}>Browse the Full Store</div>
        <p style={{ fontSize:"0.9rem", color:C.creamDim, marginBottom:18, maxWidth:400, margin:"0 auto 18px" }}>
          See all items, combo deals, and specials directly on our Square store.
        </p>
        <a
          href={storeUrl}
          target="_blank"
          rel="noreferrer"
          style={{ display:"inline-flex", alignItems:"center", gap:8, fontFamily:"'Bebas Neue',sans-serif", fontSize:"0.95rem", letterSpacing:"0.18em", color:"#fff", background:C.pink, padding:"13px 32px", borderRadius:3, textDecoration:"none" }}
        >
          🛒 Open Full Square Store ↗
        </a>
      </div>

      {/* ── CATERING CTA ── */}
      <div style={{ padding: m ? "28px 18px" : "36px 44px", textAlign:"center" }}>
        <p style={{ fontSize:"0.88rem", color:C.creamDim, marginBottom:12 }}>Need birria for a large event?</p>
        <BtnOutline onClick={() => navigate("catering")}>Book Catering for 50+ Guests</BtnOutline>
      </div>

      <Footer navigate={navigate} />
    </div>
  );
}

/* ═══════════════════════════════════════════════
   APP ROOT — fetches live content from API
═══════════════════════════════════════════════ */
export default function App() {
  const [page, setPage]             = useState("home");
  const [adminOpen, setAdminOpen]   = useState(false);
  const [content, setContent]       = useState(() => ({ ...DEFAULT_CONTENT }));
  const [menuItems, setMenuItems]   = useState(() => JSON.parse(JSON.stringify(DEFAULT_MENU_ITEMS)));
  const [orderItems, setOrderItems] = useState(() => ORDER_ITEMS.map(i=>({...i})));
  const [siteLoaded, setSiteLoaded] = useState(false);

  /* Fetch live content from API on mount */
  useEffect(() => {
    fetchSiteData().then(({ site, menu, order }) => {
      if (site)  setContent(c => ({ ...c, ...site }));
      if (menu && menu.length) setMenuItems(menu);
      if (order && order.length) setOrderItems(order);
      setSiteLoaded(true);
    }).catch(() => setSiteLoaded(true));
  }, []);

  const navigate = useCallback(p => {
    setPage(p);
    window.scrollTo({ top:0, behavior:"smooth" });
  }, []);

  /* Close admin with Escape */
  useEffect(() => {
    const h = e => { if (e.key === "Escape") setAdminOpen(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const siteValue = { content, menuItems, orderItems };

  const pages = {
    home:     <HomePage     navigate={navigate} />,
    story:    <StoryPage    navigate={navigate} />,
    menu:     <MenuPage     navigate={navigate} />,
    catering: <CateringPage navigate={navigate} />,
    reviews:  <ReviewsPage  navigate={navigate} />,
    findus:   <FindUsPage   navigate={navigate} />,
    order:    <OrderPage    navigate={navigate} />,
  };

  return (
    <SiteCtx.Provider value={siteValue}>
      <GlobalStyles />
      <Header currentPage={page} navigate={navigate} content={content} />
      <main style={{ minHeight:"100vh" }}>
        {pages[page] || pages.home}
      </main>

      {/* Floating admin button — bottom right */}
      <AdminTrigger onClick={() => setAdminOpen(true)} />

      {/* Admin panel */}
      {adminOpen && (
        <AdminPanel
          onClose={() => setAdminOpen(false)}
          navigate={navigate}
          content={content}       setContent={setContent}
          menuItems={menuItems}   setMenuItems={setMenuItems}
          orderItems={orderItems} setOrderItems={setOrderItems}
        />
      )}
    </SiteCtx.Provider>
  );
}
